import { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import styles from './FeedEditor.module.css';
import {normalizeBase64} from '../../../util';
import { nanoid } from 'nanoid';

export default function FeedEditor({ initialConti, backFunction, setStepProps, stepProps, setIsPreviewMode }) {
  const canvasRef = useRef(typeof document !== 'undefined' ? document.createElement('canvas') : null);
  const frameRef = useRef(null);
  const textRefs = useRef({});
  const fileInputRef = useRef();
  const isTextDraggingRef = useRef(false);
  const holdTimer = useRef(null);
  const overlayFileInputRef = useRef();
  const prevImageRaws = useRef([]);
  const [didInitialPositioning, setDidInitialPositioning] = useState(false);
  const [ratio, setRatio] = useState('4/5');
  const [selectedType, setSelectedType] = useState('image');
  const [selPageIdx, setSelPageIdx] = useState(0);
  const [selIconIdx,   setSelIconIdx]   = useState(null);
  const [selCaptionIdx, setSelCaptionIdx] = useState(null);
  const [selOverlayIdx, setSelOverlayIdx] = useState(null);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [showSafeArea, setShowSafeArea] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [isIconDownloading, setIsIconDownloading] = useState(false);
  const [isIconLoading, setIsIconLoading] = useState(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [showIconSearch, setShowIconSearch] = useState(false);
  const [iconQuery,      setIconQuery]      = useState('');
  const [iconResults,    setIconResults]    = useState([]);
  const [pages, setPages] = useState([]);
  const [scaleFactor, setScaleFactor] = useState(1.0);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const defaultNewPage = (raw='') => ({
    captions: [],
    icons: [],
    overlays: [],
    image: {
      raw,
      x: 0,
      y: 0,
      scale: 1,
      rotate: 0,
      brightness: 0,
      contrast: 0,
      warmth: 0,
      saturation: 0,
    },
    isTitle:false,
    image_index: null,
    source:'-',
    category:'-',
    palette: { dominant: [], lighten: [], darken: [] },
    selectedDominant: 0
  });
  const defaultNewCaption = (text = '새로운 텍스트', type = 'caption') => {
    const page = pages[selPageIdx] || defaultNewPage();
    const domIdx = page.selectedDominant || 0;
    const pal   = stepProps[8].pagePalettes[selPageIdx] || { dominant: [], darken: [] };
    return {
      type: type,
      text,
      x: 50,
      y: 50,
      rotate: 0,
      fontSize: 24,
      fontWeight: 600,
      color: '#ffffff',
      fontFamily: 'Poor Story',
      strokeWidth: 6,
      strokeColor: '#000000',
      align: 'center',
      useFillGradient: false,
      fillGradient: { startColor: '#ffffff', endColor: '#000000' },
      useStrokeGradient: true,
      strokeGradient: { startColor: pal.dominant[domIdx] || '#ffffff', endColor: pal.darken[domIdx]?.[2] || '#000000' },
      shadowEnabled: true,
      shadowColor: pal.darken[domIdx]?.[5] || '#000000',
      shadowOffsetX: 2,
      shadowOffsetY: 1,
      outerStrokeColor: '#ffffff',
      outerStrokeWidth: 12,
      zIndex: nextZ()
    }
  };
  const defaultNewIcon = (svgText = '') => {
    const svgFull = svgText.trim().startsWith('<svg') ? svgText : `<svg xmlns="http://www.w3.org/2000/svg">${svgText}</svg>`;
    const colors = extractSvgColors(svgFull);
    const customColors = [...colors];
  
    return {
      id: nanoid(),
      baseSvgText: svgFull,
      x: 0,
      y: 0,
      scale: 0.5,
      rotate: 0,
      brightness: 0,
      contrast: 0,
      saturation: 0,
      strokeWidth:  1,
      strokeColor:  '#ffffff',
      colors,
      customColors,
      zIndex: nextZ()
    };
  };
  const defaultNewOverlayImage = (raw, type='image') => ({
    id: nanoid(),
    type: type,
    raw,
    x: 0,
    y: 0,
    scale: 0.3,
    rotate: 0,
    brightness: 0,
    contrast: 0,
    saturation: 0,
    zIndex: nextZ()
  });

  const page = pages[selPageIdx] ?? defaultNewPage();
  const image = page.image ?? null;
  const caption  = selCaptionIdx != null ? page.captions[selCaptionIdx] : null;
  const icon  = selIconIdx != null ? page.icons[selIconIdx] : null;
  const overlay  = selOverlayIdx != null ? page.overlays[selOverlayIdx] : null;

  async function waitForRender() {
    // 두 번 걸치면 React 업데이트 + DOM 반영까지 거의 확실히 보장
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
  }

  let nextZ = () => {
    // scan all existing pages → all their items → find max zIndex
    const all = pages.flatMap(pg =>
      [
        ...pg.captions.map(c => c.zIndex),
        ...pg.icons   .map(i => i.zIndex),
        ...pg.overlays.map(o => o.zIndex)
      ]
    );
    return all.length ? Math.max(...all) + 1 : 1;
  };

  /*const handleIconSearch = async (keyword=null) => {
    if (!iconQuery.trim() && !keyword) return;
    const icons = await searchIcons(keyword || iconQuery);
    setIconResults(icons);
  };

  const whenIconSearchButtonClicked = () => {
    setSelCaptionIdx(null);
    setSelIconIdx(null);
    setSelOverlayIdx(null);
    setShowTextEditor(false);
    setShowIconSearch(true);
    setSelectedType('image');
  }*/

  const getNextZIndex = () => {
    const pg = pages[selPageIdx] || { captions:[], icons:[], overlays:[] };
    const all = [
      ...pg.captions.map(c => c.zIndex),
      ...pg.icons   .map(i => i.zIndex),
      ...pg.overlays.map(o => o.zIndex),
    ];
    return all.length ? Math.max(...all) + 1 : 1;
  };

  const getSelectedImages = () =>
    (stepProps[7]?.originalImages || []).map((raw, idx) => {
      if (raw.startsWith('data:')) return raw;

      let mime;
      if (raw.startsWith('R0lGOD'))       mime = 'image/gif';    // GIF 헤더
      else if (raw.startsWith('AAAA'))    mime = 'video/mp4';   // MP4 헤더(iso-base64)
      else                                 mime = 'image/jpeg';  // default
  
      return `data:${mime};base64,${raw}`;
  });

  const handleDuplicate = () => {
    const nextZ = getNextZIndex();

    if (caption != null) {
      const newCap     = { ...caption, x: caption.x + 10, y: caption.y + 10, zIndex: nextZ };
      setPages(ps =>
        ps.map((pg, idx) =>
          idx !== selPageIdx
            ? pg
            : { ...pg, captions: [...pg.captions, newCap] }
        )
      );
      setSelCaptionIdx(page.captions.length);
      setSelectedType('text');
    } else if (icon != null) {
      const newIcon      = {
        ...icon,
        id: nanoid(),
        x: icon.x + 10,
        y: icon.y + 10,
        colors:       [...icon.colors],
        customColors: [...icon.customColors],
        zIndex: nextZ
      };
    
      setPages(ps =>
        ps.map((pg, idx) =>
          idx !== selPageIdx
            ? pg
            : { ...pg, icons: [...pg.icons, newIcon] }
        )
      );
      setSelIconIdx(page.icons.length);
      setSelectedType('icon');
    } else if (overlay != null) {
      const newOv = {
        ...overlay,
        id: nanoid(),
        x: overlay.x + 10,
        y: overlay.y + 10,
        zIndex: nextZ
      };
      setPages(ps =>
        ps.map((pg, idx) =>
          idx !== selPageIdx
            ? pg
            : { ...pg, overlays: [...pg.overlays, newOv] }
        )
      );
      setSelOverlayIdx(page.overlays.length);
      setSelectedType('overlay');
    }
  };

  const fitPageToFrame = async (raw = page.image.raw, pageIndex = selPageIdx) => {
    if (raw == '') return;
  
    const isVideo = raw.startsWith('data:video/');
    let w, h;
    if (isVideo) {
      const video = document.createElement('video');
      video.src = raw;
      await new Promise(res => video.addEventListener('loadedmetadata', res, { once: true }));
      w = video.videoWidth;
      h = video.videoHeight;
    } else {
      const img = new Image();
      img.src = raw;
      await img.decode();
      w = img.naturalWidth;
      h = img.naturalHeight;
    }
  
    // ── 모바일/PC 화면에 관계없이 내부는 항상 480 ──
    const fw = 480;
    const fh = ratio === '4/5' ? 600 : 480;
  
    // 비디오 또는 이미지 원본(w,h)을 480에 맞춰 최대한 꽉 채우기
    const scaleX = fw / w;
    const scaleY = fh / h;
    const scale  = Math.max(scaleX, scaleY);
  
    // 중앙 정렬 (internal 좌표)
    const x = (fw - w) / 2;
    const y = (fh - h) / 2;
  
    updateImage({
      raw,
      scale,
      x,
      y,
      rotate:     0,
      brightness: 0,
      contrast:   0,
      warmth:     0,
      saturation: 0,
    }, pageIndex);
  };
  

  async function searchIcons(query) {
    setIsIconLoading(true);
    const res = await fetch(
      `https://api.iconfinder.com/v4/icons/search?query=${encodeURIComponent(query)}&count=100`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_ICONFINDER_KEY}`,
          accept: 'application/json'
        }
      }
    );
    const json = await res.json();

    setIsIconLoading(false);
  
    return json.icons
      .filter(icon => !icon.is_premium)
      .filter(icon => icon.vector_sizes);
  }

  const measureCaption = cap => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    ctx.font = `${cap.fontWeight} ${cap.fontSize}px ${cap.fontFamily}`;
  
    const lines  = cap.text.split(/\r?\n/);
    const width  = lines.reduce((w, line) => Math.max(w, ctx.measureText(line).width), 0);
    const height = lines.length * cap.fontSize * 1.15;
  
    return { width, height };
  };

  const centerCaptionX = (pageIdx = selPageIdx, captionIdx = selCaptionIdx) => {
    if (!pages[pageIdx]?.captions?.[captionIdx]) return;
  
    const cap = pages[pageIdx].captions[captionIdx];
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    ctx.font = `${cap.fontWeight} ${cap.fontSize}px ${cap.fontFamily}`;
    const lines = cap.text.split(/\r?\n/);
    const boxW  = lines.reduce((w, line) => Math.max(w, ctx.measureText(line).width), 0);
  
    // ── 내부 기준 480px ──
    const frameW = 480;
    const newX = (frameW - boxW) / 2;
  
    updateCaption({ x: newX }, pageIdx, captionIdx);
  };
  
  const centerCaptionY = (pageIdx = selPageIdx, captionIdx = selCaptionIdx) => {
    const cap = pages[pageIdx].captions[captionIdx];
    const frameH = ratio === '4/5' ? 600 : 480;
    const { height: boxH } = measureCaption(cap);
    const newY = (frameH - boxH) / 2;
  
    updateCaption({ y: newY }, pageIdx, captionIdx);
  };

  async function fillCaptionWidth(pageIdx = selPageIdx, captionIdx = selCaptionIdx, padding = 40) {
    await document.fonts.ready;
    const cap = pages[pageIdx].captions[captionIdx];
  
    // ── 이제 무조건 내부 기준 480px ──
    const frameW = 480;
    const { width: textBoxWidth } = measureCaption(cap);
    const targetW = frameW - padding * 2;
  
    // 텍스트 박스 전체 너비(textBoxWidth) 대비 targetW 비율만큼 fontSize 조정
    const newFontSize = cap.fontSize * (targetW / textBoxWidth);
  
    updateCaption(
      {
        fontSize: newFontSize,
        x:        padding,
        align:    'center'
      },
      pageIdx, captionIdx
    );
  }

  const centerOverlayX = (pageIdx = selPageIdx, overlayIdx = selOverlayIdx) => {
    const ov = pages[pageIdx]?.overlays?.[overlayIdx];
    if (!ov) return;
  
    // img[data-overlay-idx] 엘리먼트를 찾아야 naturalWidth를 얻을 수 있으므로,
    // waitForRender() 이후에 호출해야 함을 전제
    const el = frameRef.current.querySelector(`img[data-overlay-idx="${ov.id}"]`);
    if (!el || !el.complete) return;
  
    // ── 내부 기준: 480px ──
    const frameW = 480;
  
    // ‘실제 내부 렌더에서 사용하는 픽셀 너비’ = naturalWidth * ov.scale
    const dispW = el.naturalWidth;
  
    // 중앙 정렬: newX 위치를 정할 때도 내부 좌표 기준
    const newX = (frameW - dispW) / 2;
  
    updateOverlay({ x: newX }, pageIdx, overlayIdx);
  };
  
  const centerOverlayY = (pageIdx = selPageIdx, overlayIdx = selOverlayIdx) => {
    const ov = pages[pageIdx]?.overlays?.[overlayIdx];
    if (!ov) return;
  
    const el = frameRef.current.querySelector(`img[data-overlay-idx="${ov.id}"]`);
    if (!el || !el.complete) return;
  
    const frameH = ratio === '4/5' ? 600 : 480;
  
    // ‘실제 내부 렌더에서 사용하는 픽셀 높이’ = naturalHeight * ov.scale
    const dispH = el.naturalHeight;
  
    // 중앙 정렬
    const newY = (frameH - dispH) / 2;
  
    updateOverlay({ y: newY }, pageIdx, overlayIdx);
  };
  

  const updateImage = (props, pageIdx=selPageIdx) => {
    setPages(ps =>
      ps.map((pg, pi) => {
        if (pi !== pageIdx) return pg;
        return {
          ...pg,
          image: { ...pg.image, ...props },
        };
      })
    );
  };

  const updateCaption = (props, pageIdx=selPageIdx, captionIdx=selCaptionIdx) => {
    if (!pages[pageIdx]?.captions?.[captionIdx]) return;

    setPages(ps =>
      ps.map((pg, pi) => {
        if (pi !== pageIdx) return pg;
        return {
          ...pg,
          captions: pg.captions.map((c, ci) =>
            ci !== captionIdx
              ? c
              : { ...c, ...props }
          ),
        };
      })
    );
  };

  function updateIcon(props, pageIdx=selPageIdx, iconIdx=selIconIdx){
    setPages(ps =>
      ps.map((pg, pi) => {
        if (pi !== pageIdx) return pg;
        return {
          ...pg,
          icons: pg.icons.map((ic, ii) =>
            ii !== iconIdx ? ic : { ...ic, ...props }
          )
        };
      })
    );
  }

  function updateOverlay(props, pageIdx=selPageIdx, overlayIdx=selOverlayIdx) {
    setPages(ps =>
      ps.map((pg, pi) => {
        if (pi !== pageIdx) return pg;
        return {
          ...pg,
          overlays: pg.overlays.map((ov, oi) =>
            oi !== overlayIdx ? ov : { ...ov, ...props }
          ),
        };
      })
    );
  }

  const convertGifToMp4 = async (gifDataUrl, id) => {
    const ffmpeg = new FFmpeg({
      log: false,
      corePath: 'https://unpkg.com/@ffmpeg/core@0.12.15/dist/ffmpeg-core.js'
    });
    await ffmpeg.load();
  
    // 2. load GIF into MEMFS
    const buffer = new Uint8Array(await (await fetch(gifDataUrl)).arrayBuffer());
    const inName  = `in_${id}.gif`;
    const outName = `out_${id}.mp4`;
    await ffmpeg.writeFile(inName, buffer);
  
    // 4) 변환  
    await ffmpeg.exec([
      '-i', inName,
      '-movflags', 'faststart',
      '-c:v', 'libx264',
      '-crf', '0',
      '-preset', 'fast',
      '-pix_fmt', 'yuv420p',
      '-vf', 'scale=trunc(iw/2)*2:trunc(ih/2)*2',
      '-vsync', '0',
      outName
    ]);
  
    const { buffer: outBuf } = await ffmpeg.readFile(outName);
    await ffmpeg.terminate();      // destroys worker + clears MEMFS

    // 5. get a data‑URL
    const blob = new Blob([outBuf], { type: 'video/mp4' });
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const startHoldPreview = e => {
    const isSingleTouch = e.type === 'touchstart' && e.touches.length === 1;
    const isLeftClick  = e.type === 'mousedown'  && e.button === 0;
    if (!isSingleTouch && !isLeftClick) return;

    holdTimer.current = setTimeout(() => {
      setIsHolding(true);
    }, 500);
  };

  const endHoldPreview = () => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    if (isHolding) {
      setIsHolding(false);
    }
  };

  const addPage = () => {
    setPages(p => [...p, defaultNewPage()]);
    selectPage(pages.length);
  };

  const delPage = idx => {
    const result = window.confirm('이 페이지를 삭제할까요?');
    if(!result)
      return;

    if (pages.length === 1) return;
    setPages(p => p.filter((_, i) => i !== idx));
    selectPage(i => Math.max(0, i - 1));
  };
  
  const movePage = (from, to) => {
    if (to < 0 || to >= pages.length) return;
    setPages(p => {
      const arr = [...p];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
    selectPage(to);
  };

  const whenResetImageButtonClicked = async () => {
    const result = window.confirm('현재 페이지 내 배경 이미지의 스타일이 초기상태로 복구됩니다.\n계속하시겠습니까?');
    if(!result)
      return;

    await fitPageToFrame();
  };

  const whenResetCaptionButtonClicked = async () => {
    const result = window.confirm('현재 페이지 내 캡션들의 스타일이 초기상태로 복구됩니다.\n계속하시겠습니까?');
    if(!result)
      return;

    await template1(selPageIdx);
  };

  // 썸네일 클릭
  const selectPage = idx => {
    setShowTextEditor(false);
    setSelCaptionIdx(null);
    setSelIconIdx(null);
    setSelOverlayIdx(null);
    setSelectedType('image');
    setSelPageIdx(idx);
  };
  
  function adjustZ(delta) {
    // 1) figure out which array and index we’re editing
    let arrName, selIdx;
    if (selectedType === 'text') {
      arrName = 'captions';   selIdx = selCaptionIdx;
    } else if (selectedType === 'icon') {
      arrName = 'icons';      selIdx = selIconIdx;
    } else if (selectedType === 'overlay') {
      arrName = 'overlays';   selIdx = selOverlayIdx;
    } else {
      return; // nothing selected
    }
    if (selIdx == null) return;
  
    setPages(pages =>
      pages.map((pg, pi) => {
        if (pi !== selPageIdx) return pg;
  
        // 2) flatten current page into one list
        const items = [
          ...pg.captions.map((c,i) => ({ type: 'text',    idx: i, z: c.zIndex })),
          ...pg.icons   .map((c,i) => ({ type: 'icon',    idx: i, z: c.zIndex })),
          ...pg.overlays.map((c,i) => ({ type: 'overlay', idx: i, z: c.zIndex }))
        ];
        // 3) sort by zIndex
        items.sort((a,b) => a.z - b.z);
  
        // 4) find our item in that sorted list
        const meType = selectedType;  // 'text' | 'icon' | 'overlay'
        const me = items.find(it => it.type === meType && it.idx === selIdx);
        if (!me) return pg;
  
        const pos = items.indexOf(me);
        const swapPos = pos + (delta > 0 ? 1 : -1);
        if (swapPos < 0 || swapPos >= items.length) {
          // out of bounds → can’t move
          return pg;
        }
        const other = items[swapPos];
  
        // 5) build new arrays with zIndex swapped
        function swapIn(arr, type, idx, zNew) {
          return arr.map((el,i) =>
            type === meType && i === me.idx
              ? { ...el, zIndex: other.z }
              : el
          );
        }
  
        // We need two swaps: me ↔ other
        // To keep it simple, let’s just manually swap both:
        const swapMe = (arr, type, idx, newZ) =>
          arr.map((el,i) =>
            (type === meType &&     i === me.idx)   ||
            (type === other.type && i === other.idx)
              ? { ...el, zIndex:
                  (type === meType && i === me.idx)    ? other.z
                : (type === other.type && i === other.idx) ? me.z
                : el.zIndex
                }
              : el
          );
  
        return {
          ...pg,
          captions: swapMe(pg.captions, 'text',    selCaptionIdx, pg.captions[selCaptionIdx]?.zIndex),
          icons:    swapMe(pg.icons,    'icon',    selIconIdx,     pg.icons[selIconIdx]?.zIndex),
          overlays: swapMe(pg.overlays, 'overlay', selOverlayIdx,  pg.overlays[selOverlayIdx]?.zIndex)
        };
      })
    );
  }

  // Utility functions
  const initCanvasAndScale = () => {
    const frameW = 480;
    const frameH = ratio === '4/5' ? 600 : 480;

    const targetW = 1080;
    const targetH = ratio === '4/5'
      ? Math.round((1080 * 5) / 4)
      : 1080;
    const canvas = canvasRef.current;
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    const scaleX = targetW / frameW;
    const scaleY = targetH / frameH;

    const frameRect = { width: frameW, height: frameH };

    return { ctx, frameRect, targetW, targetH, scaleX, scaleY };
  };

  const applyFilter = (ctx, { brightness, contrast, saturation }) => {
    ctx.filter = [
      `brightness(${1 + brightness})`,
      `contrast(${1 + contrast})`,
      `saturate(${1 + saturation})`
    ].join(' ');
  };

  // FeedEditor 컴포넌트 내부, useState/useRef 선언 아래에 위치해야 합니다.
  // (frameRef, scaleFactors, ratio 등이 이미 props/state로 선언되어 있다고 가정)

  const computePivot = (elRect, scaleX, scaleY) => {
    // ── ① 실제 DOM상 frame 위치 정보 ──
    // frameRect 자체는 { width: 480, height: 600 } 처럼 "내부 기준 크기"를 담고 있다 가정
    const frameDom = frameRef.current.getBoundingClientRect();

    // ── ② 화면 상의 el 중심(픽셀) 좌표 ──
    //    (elRect.left, elRect.top은 화면 픽셀 좌표임)
    const elCenterX_inScreen = elRect.left + elRect.width  / 2;
    const elCenterY_inScreen = elRect.top  + elRect.height / 2;

    // ── ③ 화면 상 프레임의 시작 위치(픽셀) ──
    const frameLeft_inScreen = frameDom.left;
    const frameTop_inScreen  = frameDom.top;

    // ── ④ 화면 상 중심점을 “프레임 내부 좌표(480×600 기준)”로 환산 ──
    //    화면 상의 가로 픽셀 1px이 내부 좌표 상 1/sf만큼으로 대응됨
    const sf = scaleFactor; // 이미 useState로 계산해 두었던 “renderArea 높이 ÷ 600” 혹은 “renderArea 너비 ÷ 480” 값

    const internalCenterX = (elCenterX_inScreen - frameLeft_inScreen) / sf;
    const internalCenterY = (elCenterY_inScreen - frameTop_inScreen)  / sf;

    // ── ⑤ 내부 좌표(480×600 기준) → 캔버스 픽셀 좌표 변환 ──
    //    scaleX = (1080 또는 targetW) ÷ 480
    //    scaleY = (targetH) ÷ 600
    return {
      x: internalCenterX * scaleX,
      y: internalCenterY * scaleY,
    };
  };


  const drawMedia = (ctx, mediaEl, pivot, { rotate, scale }, scaleX, scaleY) => {
    ctx.save();
    ctx.translate(pivot.x, pivot.y);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale(scale * scaleX, scale * scaleY);
    const w = mediaEl instanceof HTMLVideoElement
      ? mediaEl.videoWidth
      : mediaEl.width;
    const h = mediaEl instanceof HTMLVideoElement
      ? mediaEl.videoHeight
      : mediaEl.height;
    ctx.drawImage(mediaEl, -w / 2, -h / 2);
    ctx.restore();
  };

  const getBBoxes = captions =>
  captions.map((_, idx) => {
    const textEl = textRefs.current[idx];
    if (!textEl) {
      console.warn(`Caption #${idx} element not found; using zero bbox.`);
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    return textEl.getBBox();
  });

  const handleIconSelect = async (icon) => {
    setIsIconDownloading(true);
    const largestVector = icon.vector_sizes[icon.vector_sizes.length - 1];
    const svgFormat     = largestVector.formats.find(f => f.format === 'svg');
    const downloadUrl   = svgFormat.download_url;
    const { pathname }  = new URL(downloadUrl);
    const res = await fetch(`/iconfinder${pathname}`, {
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_ICONFINDER_KEY}`,
      }
    });
    const raw = await res.text();
    const svgOnly = raw.slice(raw.indexOf('<svg'));
  
    setPages(ps =>
      ps.map((pg, i) =>
        i === selPageIdx
          ? {
              ...pg,
              icons: [...pg.icons, defaultNewIcon(svgOnly)]
            }
          : pg
      )
    );
    setSelIconIdx(page.icons.length);
    setSelectedType('icon')
    setSelCaptionIdx(null);
    setShowIconSearch(false);
    setIsIconDownloading(false);
  };
  

  // 2) 아이콘 썸네일 렌더링 함수
  const renderIconThumb = (icon) => {
    const previewUrl = icon.raster_sizes?.[5]?.formats?.[0]?.preview_url;
    return (
      <img
        key={icon.id}
        src={previewUrl}
        className={styles.iconThumb}
        onClick={() => handleIconSelect(icon)}
      />
    );
  };

  const drawCaptions = (ctx, captions, bboxes, scaleX, scaleY) => {
    ctx.textBaseline = 'alphabetic';
  
    captions.forEach((cap, idx) => {
      const { width: tw, height: th } = measureCaption(cap);

      const pivotX = (cap.x + tw / 2) * scaleX;
      const pivotY = (cap.y + th / 2) * scaleY;
  
      ctx.save();
      ctx.translate(pivotX, pivotY);
      ctx.rotate((cap.rotate * Math.PI) / 180);
      ctx.translate(-pivotX, -pivotY);

      if (cap.outerStrokeWidth > 0) {
        ctx.save();
        // 편집처럼, 그림자 설정 시 offset/2 이동
        if (cap.shadowEnabled) {
          ctx.translate(
            (cap.shadowOffsetX * scaleX) / 2,
            (cap.shadowOffsetY * scaleY) / 2
          );
        }
        ctx.lineWidth   = cap.outerStrokeWidth * scaleX;
        ctx.strokeStyle = cap.outerStrokeColor;
        ctx.lineJoin    = 'round';
        ctx.lineCap     = 'round';
        ctx.font        = `${cap.fontWeight} ${cap.fontSize * scaleX}px ${cap.fontFamily}`;
        ctx.textAlign   = cap.align;
  
        // 캡션 라인별 stroke
        cap.text.split(/\r?\n/).forEach((line, i) => {
          // bboxes[idx] 는 단지 텍스트 내부 정렬용으로 그대로 씁니다
          const { x: bx, y: by, width: bw, height: bh } = bboxes[idx];
          let x = (cap.x + bx) * scaleX;
          if      (cap.align === 'center') x += (bw / 2) * scaleX;
          else if (cap.align === 'right')  x += bw * scaleX;
          const y = (cap.y + cap.fontSize + i * (cap.fontSize * 1.15)) * scaleY;
          ctx.strokeText(line, x, y);
        });
        ctx.restore();
      }
  
      if (cap.shadowEnabled) {
        // ── 1) SOLID STROKE + SHADOW 패스 ──
        if (cap.useStrokeGradient) {
          ctx.save();
          // 그림자 세팅
          ctx.shadowColor   = cap.shadowColor;
          ctx.shadowOffsetX = cap.shadowOffsetX * scaleX;
          ctx.shadowOffsetY = cap.shadowOffsetY * scaleY;
          ctx.shadowBlur    = 0;  // iOS에서 0 블러는 무시하므로 0 또는 1 모두 OK
      
          // 솔리드 스트로크 (그림자만)
          ctx.lineWidth   = cap.strokeWidth * scaleX;
          ctx.lineJoin    = 'round';
          ctx.lineCap     = 'round';
          ctx.font        = `${cap.fontWeight} ${cap.fontSize * scaleX}px ${cap.fontFamily}`;
          ctx.textAlign   = cap.align;
          ctx.strokeStyle = cap.shadowColor;
      
          cap.text.split(/\r?\n/).forEach((line, i) => {
            // x,y 계산은 원래와 동일
            const { x: bx, width: bw } = bboxes[idx];
            let x = (cap.x + bx) * scaleX;
            if      (cap.align === 'center') x += (bw / 2) * scaleX;
            else if (cap.align === 'right')  x += bw * scaleX;
            const y = (cap.y + cap.fontSize + i * (cap.fontSize * 1.15)) * scaleY;
      
            ctx.strokeText(line, x, y);
          });
          ctx.restore();
        }
      
        // ── 2) GRADIENT STROKE 패스 (그림자 제거) ──
        if (cap.useStrokeGradient) {
          ctx.save();
          // 그림자 완전히 끄기
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur  = 0;
      
          // gradient stroke
          ctx.lineWidth   = cap.strokeWidth * scaleX;
          ctx.lineJoin    = 'round';
          ctx.lineCap     = 'round';
          ctx.font        = `${cap.fontWeight} ${cap.fontSize * scaleX}px ${cap.fontFamily}`;
          ctx.textAlign   = cap.align;
          const grad = ctx.createLinearGradient(
            0, (cap.y) * scaleY,
            0, (cap.y + th) * scaleY
          );
          grad.addColorStop(0, cap.strokeGradient.startColor);
          grad.addColorStop(1, cap.strokeGradient.endColor);
          ctx.strokeStyle = grad;
      
          cap.text.split(/\r?\n/).forEach((line, i) => {
            const { x: bx, width: bw } = bboxes[idx];
            let x = (cap.x + bx) * scaleX;
            if      (cap.align === 'center') x += (bw / 2) * scaleX;
            else if (cap.align === 'right')  x += bw * scaleX;
            const y = (cap.y + cap.fontSize + i * (cap.fontSize * 1.15)) * scaleY;
      
            ctx.strokeText(line, x, y);
          });
          ctx.restore();
        } else {
          // 그라데이션이 아닌 경우 원래 그대로 그림자＋솔리드 스트로크
          ctx.save();
          ctx.shadowColor   = cap.shadowColor;
          ctx.shadowOffsetX = cap.shadowOffsetX * scaleX;
          ctx.shadowOffsetY = cap.shadowOffsetY * scaleY;
          ctx.shadowBlur    = 0;
      
          ctx.lineWidth   = cap.strokeWidth * scaleX;
          ctx.lineJoin    = 'round';
          ctx.lineCap     = 'round';
          ctx.font        = `${cap.fontWeight} ${cap.fontSize * scaleX}px ${cap.fontFamily}`;
          ctx.textAlign   = cap.align;
          ctx.strokeStyle = cap.strokeColor;
      
          cap.text.split(/\r?\n/).forEach((line, i) => {
            const { x: bx, width: bw } = bboxes[idx];
            let x = (cap.x + bx) * scaleX;
            if      (cap.align === 'center') x += (bw / 2) * scaleX;
            else if (cap.align === 'right')  x += bw * scaleX;
            const y = (cap.y + cap.fontSize + i * (cap.fontSize * 1.15)) * scaleY;
      
            ctx.strokeText(line, x, y);
          });
          ctx.restore();
        }
      
        // ── 3) FILL TEXT ──
        ctx.save();
        if (cap.useFillGradient) {
          const fg = ctx.createLinearGradient(
            0, (cap.y) * scaleY,
            0, (cap.y + th) * scaleY
          );
          fg.addColorStop(0, cap.fillGradient.startColor);
          fg.addColorStop(1, cap.fillGradient.endColor);
          ctx.fillStyle = fg;
        } else {
          ctx.fillStyle = cap.color;
        }
        ctx.font      = `${cap.fontWeight} ${cap.fontSize * scaleX}px ${cap.fontFamily}`;
        ctx.textAlign = cap.align;
        cap.text.split(/\r?\n/).forEach((line, i) => {
          const { x: bx, width: bw } = bboxes[idx];
          let x = (cap.x + bx) * scaleX;
          if      (cap.align === 'center') x += (bw / 2) * scaleX;
          else if (cap.align === 'right')  x += bw * scaleX;
          const y = (cap.y + cap.fontSize + i * (cap.fontSize * 1.15)) * scaleY;
          ctx.fillText(line, x, y);
        });
        ctx.restore();
      }

      if (cap.strokeWidth > 0) {
        ctx.save();
        ctx.lineWidth = cap.strokeWidth * scaleX;
        ctx.lineJoin  = 'round';
        ctx.lineCap   = 'round';
        ctx.font      = `${cap.fontWeight} ${cap.fontSize * scaleX}px ${cap.fontFamily}`;
        ctx.textAlign = cap.align;
    
        if (cap.useStrokeGradient) {
          const g = ctx.createLinearGradient(
            0, (cap.y) * scaleY,
            0, (cap.y + th) * scaleY
          );
          g.addColorStop(0, cap.strokeGradient.startColor);
          g.addColorStop(1, cap.strokeGradient.endColor);
          ctx.strokeStyle = g;
        } else {
          ctx.strokeStyle = cap.strokeColor;
        }
    
        cap.text.split(/\r?\n/).forEach((line, i) => {
          const { x: bx, y: by, width: bw } = bboxes[idx];
          let x = (cap.x + bx) * scaleX;
          if      (cap.align === 'center') x += (bw / 2) * scaleX;
          else if (cap.align === 'right')  x += bw * scaleX;
          const y = (cap.y + cap.fontSize + i * (cap.fontSize * 1.15)) * scaleY;
          ctx.strokeText(line, x, y);
        });
        ctx.restore();
      }

      ctx.save();
      if (cap.useFillGradient) {
        const fg = ctx.createLinearGradient(
          0, (cap.y) * scaleY,
          0, (cap.y + th) * scaleY
        );
        fg.addColorStop(0, cap.fillGradient.startColor);
        fg.addColorStop(1, cap.fillGradient.endColor);
        ctx.fillStyle = fg;
      } else {
        ctx.fillStyle = cap.color;
      }
      ctx.font      = `${cap.fontWeight} ${cap.fontSize * scaleX}px ${cap.fontFamily}`;
      ctx.textAlign = cap.align;
  
      cap.text.split(/\r?\n/).forEach((line, i) => {
        const { x: bx, width: bw } = bboxes[idx];
        let x = (cap.x + bx) * scaleX;
        if      (cap.align === 'center') x += (bw / 2) * scaleX;
        else if (cap.align === 'right')  x += bw * scaleX;
        const y = (cap.y + cap.fontSize + i * (cap.fontSize * 1.15)) * scaleY;
        ctx.fillText(line, x, y);
      });
      ctx.restore();
  
      // ── 회전 블록 닫기 ──
      ctx.restore();
    });
  };

  async function captureSvgViaDataUrl(svgEl, ic) {
    if (!svgEl) return null;
  
    // 1) DOM에서 실제 보이는 픽셀 크기
    const origRect = svgEl.getBoundingClientRect();
  
    // 2) viewBox 정보 파싱 (없으면 getBBox() fallback)
    let minX, minY, vbW, vbH;
    const vb = svgEl.getAttribute('viewBox');
    if (vb) {
      [minX, minY, vbW, vbH] = vb.split(/[\s,]+/).map(Number);
    } else {
      const bbox = svgEl.getBBox();
      minX = bbox.x; minY = bbox.y;
      vbW = bbox.width; vbH = bbox.height;
    }
  
    // 3) clone 해서 filter 적용 후 실제 크기 측정
    const clone = svgEl.cloneNode(true);
    clone.removeAttribute('style');
    clone.style.position   = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.top        = '0';
    clone.style.left       = '0';
    document.body.appendChild(clone);
    const newRect = clone.getBoundingClientRect();
    document.body.removeChild(clone);
  
    // 4) 진짜 패딩(px) 계산
    const padPx = (newRect.width - origRect.width) / 2;
  
    // 5) user-space 단위로 환산
    const padUserX = padPx * (vbW / origRect.width);
    const padUserY = padPx * (vbH / origRect.height);
  
    // 6) viewBox 확장
    clone.setAttribute(
      'viewBox',
      `${minX - padUserX} ${minY - padUserY} ${vbW + padUserX*2} ${vbH + padUserY*2}`
    );
  
    // 7) 캡처될 최종 픽셀 크기 지정
    clone.setAttribute('width',  newRect.width);
    clone.setAttribute('height', newRect.height);
    clone.setAttribute('style',  'overflow: visible;');
  
    // 8) CSS 필터만 inner <g>에 적용
    const innerGroup = clone.querySelector('g:not([filter])');
    if (innerGroup) {
      innerGroup.setAttribute(
        'style',
        `filter:
          brightness(${1 + ic.brightness})
          contrast(${1 + ic.contrast})
          saturate(${1 + ic.saturation});`
      );
    }
  
    // 9) serialize → Image 객체
    const svgString = new XMLSerializer().serializeToString(clone);
    const blob      = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url       = URL.createObjectURL(blob);
    const img       = new Image();
    await new Promise(res => { img.onload = res; img.src = url; });
    URL.revokeObjectURL(url);
  
    return { img, padPx };
  }
  

  // FeedEditor 내 함수들
  const saveImagePage = async (pg) => {
    // ── 1) 이미지 로딩 ─────────────────────────────────────────────────────────
    const imgEl = frameRef.current.querySelector('img, video');
    const img   = new Image();
    img.src     = pg.image.raw;
    await img.decode();
  
    // ── 2) 캔버스 & 해상도 셋업 ───────────────────────────────────────────────
    const { ctx, frameRect, targetW, targetH, scaleX, scaleY } = initCanvasAndScale();
    ctx.clearRect(0, 0, targetW, targetH);
  
    // ── 3) 배경 이미지 & 필터 그리기 ─────────────────────────────────────────
    ctx.save();
    applyFilter(ctx, pg.image);
    const imgRect = imgEl.getBoundingClientRect();
    const pivot   = computePivot(imgRect, scaleX, scaleY);
    drawMedia(ctx, img, pivot, pg.image, scaleX, scaleY);
    ctx.restore();
  
    // ── 4) Warmth overlay ────────────────────────────────────────────────────
    if (pg.image.warmth !== 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'overlay';
      ctx.fillStyle =
        pg.image.warmth < 0
          ? `rgba(0,128,255,${-pg.image.warmth * 0.5})`
          : `rgba(255,128,0,${pg.image.warmth * 0.5})`;
      ctx.fillRect(0, 0, targetW, targetH);
      ctx.restore();
    }
  
    // ── 5) Prepare icon images ────────────────────────────────────────────────
    //    so we can draw them in sorted order
    const iconDatas = await Promise.all(
      pg.icons.map(async ic => {
        const svgEl = frameRef.current.querySelector(`svg[data-icon-idx="${ic.id}"]`);
        return await captureSvgViaDataUrl(svgEl, ic);
      })
    );
  
    // ── 6) Precompute caption bboxes ───────────────────────────────────────────
    const captionBBoxes = getBBoxes(pg.captions);
  
    // ── 7) Flatten & sort everything by zIndex ───────────────────────────────
    const drawables = [
      ...pg.icons   .map((ic, i) => ({ type: 'icon',    obj: ic,  idx: i })),
      ...pg.captions.map((cap,i) => ({ type: 'caption', obj: cap, idx: i })),
      ...pg.overlays.map((ov, i) => ({ type: 'overlay', obj: ov,  idx: i }))
    ];
    drawables.sort((a, b) => a.obj.zIndex - b.obj.zIndex);
  
    // ── 8) Draw each in turn ─────────────────────────────────────────────────
    for (const item of drawables) {
      const { type, obj, idx } = item;
  
      if (type === 'icon') {
        // draw icon #idx
        const { img: iconImg } = iconDatas[idx];
        if (!iconImg) continue;
  
        const ic    = obj;
        const svgEl = frameRef.current.querySelector(
          `svg[data-icon-idx="${ic.id}"]`
        );

        const r     = svgEl.getBoundingClientRect();
        const { x: pivotX, y: pivotY } = computePivot(r, scaleX, scaleY);
  
        // compute destW/destH exactly as before
        const rad    = (ic.rotate * Math.PI) / 180;
        const absCos = Math.abs(Math.cos(rad));
        const absSin = Math.abs(Math.sin(rad));
        const w_screen = iconImg.naturalWidth;   // 화면 상으로 캡처된 (축소된) 너비
        const h_screen = iconImg.naturalHeight;  // 화면 상으로 캡처된 (축소된) 높이

        // → 내부 에디터 기준(480×600)에서의 진짜 픽셀 크기:
        const w = w_screen / scaleFactor;
        const h = h_screen / scaleFactor
        const widthFactor  = absCos * w + absSin * h;
        const heightFactor = absSin * w + absCos * h;
        const destW = (w * scaleX) * (w / widthFactor);
        const destH = (h * scaleY) * (h / heightFactor);
  
        ctx.save();
        ctx.translate(pivotX, pivotY);
        ctx.rotate(rad);
        ctx.drawImage(iconImg, -destW/2, -destH/2, destW, destH);
        ctx.restore();
  
      } else if (type === 'caption') {
        // draw caption #idx
        const cap = obj;
        // drawCaptions expects arrays; we pass one caption + its bbox
        drawCaptions(
          ctx,
          [cap],
          [captionBBoxes[idx]],
          scaleX,
          scaleY
        );
  
      } else { // overlay
        // draw overlay #idx
        const ov = obj;
        const overlayEl = frameRef.current.querySelector(
          `img[data-overlay-idx="${ov.id}"]`
        );
        if (!overlayEl) continue;
  
        const overlayImg = new Image();
        overlayImg.src = ov.raw;
        await overlayImg.decode();
  
        const r = overlayEl.getBoundingClientRect();
        const pivotOv = computePivot(r, scaleX, scaleY);
  
        ctx.save();
        ctx.filter = [
          `brightness(${1 + ov.brightness})`,
          `contrast(${1 + ov.contrast})`,
          `saturate(${1 + ov.saturation})`
        ].join(' ');
        drawMedia(
          ctx,
          overlayImg,
          pivotOv,
          { rotate: ov.rotate, scale: ov.scale },
          scaleX,
          scaleY
        );
        ctx.restore();
      }
    }
  
    // ── 9) Blob → 다운로드 ─────────────────────────────────────────────────────
    const dataUrl = canvasRef.current.toDataURL('image/jpeg', 1.0);
    setStepProps(prev => {
      const next = [...prev];
      const arr = next[8]?.result || [];
      next[8] = { ...next[8], result: [...arr, dataUrl] };
      return next;
    });
  };

  const convertTo60fps = async (videoBlob, id) => {
    // 1) FFmpeg 인스턴스 생성 (혹은 재사용)
    const ffmpeg = new FFmpeg({
      log: false,
      corePath: 'https://unpkg.com/@ffmpeg/core@0.12.15/dist/ffmpeg-core.js'
    });
    await ffmpeg.load();

    // 2) MEMFS에 기록
    const inName  = `in_${id}.webm`;   // 혹은 .mp4
    const outName = `out_${id}.mp4`;
    const data    = new Uint8Array(await videoBlob.arrayBuffer());
    await ffmpeg.writeFile(inName, data);

    // 3) 재인코딩: -r 60, -vsync cfr 로 CFR 강제
    await ffmpeg.exec([
      '-i',  inName,
      '-r',  '30',
      '-c:v','libx264',
      '-preset','superfast',
      '-movflags','faststart',
      '-vsync','cfr',
      outName
    ]);

    // 4) 결과 읽어와서 data-URL로 반환
    const { buffer: outBuf } = await ffmpeg.readFile(outName);
    await ffmpeg.terminate();

    const outBlob = new Blob([outBuf], { type: 'video/mp4' });
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(outBlob);
    });
  };

  const saveVideoPage = (pg) => {
    return new Promise(async resolve => {
      await waitForRender();

      // 1) 비디오 준비
      const videoEl = frameRef.current.querySelector('video');
      videoEl.loop = false;
      if (videoEl.readyState < 2) {
        await new Promise(r =>
          videoEl.addEventListener('loadeddata', r, { once: true })
        );
      }
      videoEl.currentTime = 0;
  
      // 2) 아이콘용 캡처 데이터 미리 만들기
      const iconDatas = await Promise.all(
        pg.icons.map(async ic => {
          const svgEl = frameRef.current.querySelector(
            `svg[data-icon-idx="${ic.id}"]`
          );
          return await captureSvgViaDataUrl(svgEl, ic);
        })
      );
  
      // 3) 오버레이 이미지 미리 로드
      const overlayImgs = await Promise.all(
        pg.overlays.map(async ov => {
          const img = new Image();
          img.src = ov.raw;
          await img.decode();
          return img;
        })
      );
  
      // 4) 캔버스 & 해상도 셋업
      const { ctx, frameRect, targetW, targetH, scaleX, scaleY } =
        initCanvasAndScale();
  
      // 5) 캡션 BBox 미리 계산 (drawCaptions 에 필요)
      await waitForRender();
      const bboxes = getBBoxes(pg.captions);
  
      // 6) “무엇을 그릴지” 한 배열로 합치고 zIndex 순으로 정렬
      const drawables = [
        ...pg.overlays.map((ov, i) => ({ type: 'overlay', obj: ov, idx: i })),
        ...pg.icons.map((ic, i) => ({ type: 'icon',    obj: ic, idx: i })),
        ...pg.captions.map((cap, i) => ({ type: 'caption', obj: cap, idx: i }))
      ].sort((a, b) => a.obj.zIndex - b.obj.zIndex);
  
      // 7) MediaRecorder 준비
      const stream  = canvasRef.current.captureStream();
      const mp4Type = 'video/mp4; codecs="avc1.42E01E"';
      const mimeType = MediaRecorder.isTypeSupported(mp4Type)
        ? mp4Type
        : 'video/webm; codecs=vp9';
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 8_000_000
      });
      const chunks = [];
      recorder.ondataavailable = e => chunks.push(e.data);
      recorder.onstop = async () => {
        isRecording = false;
        cancelAnimationFrame(animId);

        const blob = new Blob(chunks, { type: mimeType });

        // ② 60fps로 재인코딩
        //    pages[i]나 pg.id 같은 고유값을 id로 넘기세요
        const mp4DataUrl = await convertTo60fps(blob, `page${pg.index}`);

        const extractFirstFrameJpeg = async (videoDataUrl) => {
          return new Promise(resolve => {
            const vid = document.createElement('video');
            vid.src = videoDataUrl;
            vid.muted = true;
            vid.playsInline = true;

            // wait until enough data to grab dimensions & metadata
            vid.addEventListener('loadeddata', async () => {
              // ensure we're at t=0
              vid.currentTime = 0;
              await new Promise(r => vid.addEventListener('seeked', r, { once: true }));

              // draw to canvas at full resolution
              const canvas = document.createElement('canvas');
              canvas.width  = vid.videoWidth;
              canvas.height = vid.videoHeight;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(vid, 0, 0);

              // export max-quality JPEG
              const jpgDataUrl = canvas.toDataURL('image/jpeg', 1.0);
              resolve(jpgDataUrl);
            }, { once: true });
          });
        };

        const jpgDataUrl = await extractFirstFrameJpeg(mp4DataUrl);

        // ③ state에 저장하고 resolve
        setStepProps(prev => {
          const next = [...prev];
          const existing = next[8] || {};
          next[8] = {
            ...existing,
            // the array of MP4 data-URLs
            result: [
              ...(existing.result || []),
              mp4DataUrl
            ],
            // your new array of JPEG previews
            video_preview: [
              ...(existing.video_preview || []),
              jpgDataUrl
            ]
          };
          return next;
        });
        videoEl.loop = true;
        resolve();
      };
  
      // 8) 프레임 그리기 + 녹화
      let animId;
      let isRecording = true;

      const drawFrame = async () => {
        if (!isRecording) return;
        ctx.clearRect(0, 0, targetW, targetH);
  
        // 8-1) 배경 비디오 + 필터
        ctx.save();
        applyFilter(ctx, pg.image);
        const videoRect = videoEl.getBoundingClientRect();
        const pivotV    = computePivot(videoRect, scaleX, scaleY);
        drawMedia(ctx, videoEl, pivotV, pg.image, scaleX, scaleY);
        ctx.restore();
  
        // 8-2) warmth overlay
        if (pg.image.warmth !== 0) {
          ctx.save();
          ctx.globalCompositeOperation = 'overlay';
          ctx.fillStyle =
            pg.image.warmth < 0
              ? `rgba(0,128,255,${-pg.image.warmth * 0.5})`
              : `rgba(255,128,0,${pg.image.warmth * 0.5})`;
          ctx.fillRect(0, 0, targetW, targetH);
          ctx.restore();
        }
  
        // 8-3) 글로벌 zIndex 순서대로 캡션/아이콘/오버레이 그리기
        for (const item of drawables) {
          const { type, obj, idx } = item;
  
          if (type === 'overlay') {
            // ── 오버레이 ───────────────────────────────────────
            const ov     = obj;
            const ovImg  = overlayImgs[idx];
            const el     = frameRef.current.querySelector(
              `img[data-overlay-idx="${ov.id}"]`
            );
            if (!el) continue;
            const r      = el.getBoundingClientRect();
            const pivotO = computePivot(r, scaleX, scaleY);
  
            ctx.save();
            ctx.filter = [
              `brightness(${1 + ov.brightness})`,
              `contrast(${1 + ov.contrast})`,
              `saturate(${1 + ov.saturation})`
            ].join(' ');
            drawMedia(ctx, ovImg, pivotO, { rotate: ov.rotate, scale: ov.scale }, scaleX, scaleY);
            ctx.restore();
  
          } else if (type === 'icon') {
            // ── 아이콘 ─────────────────────────────────────────
            const ic  = obj;
            const { img: iconImg } = iconDatas[idx];
            if (!iconImg) continue;
            const svgEl = frameRef.current.querySelector(
              `svg[data-icon-idx="${ic.id}"]`
            );
            
            const r     = svgEl.getBoundingClientRect();
            const { x: pivotX, y: pivotY } = computePivot(r, scaleX, scaleY);
            const rad    = (ic.rotate * Math.PI) / 180;
            const absCos = Math.abs(Math.cos(rad));
            const absSin = Math.abs(Math.sin(rad));
            const w_screen = iconImg.naturalWidth;   // 화면 상으로 캡처된 (축소된) 너비
            const h_screen = iconImg.naturalHeight;  // 화면 상으로 캡처된 (축소된) 높이

            // → 내부 에디터 기준(480×600)에서의 진짜 픽셀 크기:
            const w = w_screen / scaleFactor;
            const h = h_screen / scaleFactor
            const widthFactor  = absCos * w + absSin * h;
            const heightFactor = absSin * w + absCos * h;
            const destW = (w * scaleX) * (w  / widthFactor);
            const destH = (h * scaleY) * (h  / heightFactor);
  
            ctx.save();
            ctx.translate(pivotX, pivotY);
            ctx.rotate(rad);
            ctx.drawImage(iconImg, -destW/2, -destH/2, destW, destH);
            ctx.restore();
  
          } else {
            // ── 캡션 ───────────────────────────────────────────
            const cap = obj;
            // drawCaptions 에는 단일 caption + 단일 bbox 전달
            drawCaptions(ctx, [cap], [bboxes[idx]], scaleX, scaleY);
          }
        }
  
        animId = requestAnimationFrame(drawFrame);
      };
  
      const duration = videoEl.duration;
      const MAX_DURATION = 59;
      const MIN_DURATION = 4;

      let playbackCount = 1;
      let totalPlayTime = duration;

      // 최소 길이 보장: 3초 넘길 때까지 반복 재생
      if (duration < MIN_DURATION) {
        while (totalPlayTime < MIN_DURATION) {
          totalPlayTime += duration;
          playbackCount++;
        }
      }

      // 최대 길이 제한: 60초 초과하면 잘라내기
      if (totalPlayTime > MAX_DURATION) {
        totalPlayTime = MAX_DURATION;
      }

      // 녹화 stop 예약
      setTimeout(() => {
        isRecording = false;
        recorder.stop();
      }, totalPlayTime * 1000);

      // 반복 재생 카운트 조절
      let playTimes = 0;
      videoEl.addEventListener('ended', async () => {
        playTimes++;
        if (playTimes < playbackCount) {
          videoEl.currentTime = 0;
          await videoEl.play();
        }
      }, { passive: true });

      await videoEl.play();
      recorder.start();
      drawFrame();
    });
  };

  // 저장
  const handleSave = async () => {
    if(pages.length>10){
      alert(`피드 콘텐츠는 최대 10장을 넘길 수 없습니다!\n(현재 ${pages.length}장)`);
      return;
    }

    setStepProps(prev => {
      const next = [...prev]
      next[8] = {
        ...next[8],
        result: [],
        pages
      }
      if (prev[8].file) {
        next[8].file = pages
      }
      return next
    })

    setIsRendering(true);
    await document.fonts.ready;

    for (let i = 0; i < pages.length; i++) {
      selectPage(i);
      // React가 현재 페이지 렌더링을 반영할 시간을 줍니다
      await waitForRender();

      let pg = pages[i];
      const raw = pg.image.raw;

      if (!raw) {
        continue;
      }

      if (raw.startsWith('data:image/gif')) {
        const mp4Raw = await convertGifToMp4(raw, i);

        // ① 상태 업데이트
        setPages(ps =>
          ps.map((p, idx) =>
            idx === i
              ? { ...p, image: { ...p.image, raw: mp4Raw } }
              : p
          )
        );

        // ② 다시 렌더링 기다림
        await waitForRender();

        pg = { ...pg, image: { ...pg.image, raw: mp4Raw } };
      }

      // 이제 DOM에는 <video>가 확실히 있을 거예요
      if (pg.image.raw.startsWith('data:video/')) {
        await saveVideoPage(pg);
      } else {
        await saveImagePage(pg);
      }
    }

    selectPage(0);
    setIsRendering(false);

    setIsPreviewMode(true);
  };
  
  const handleLoadFromOS = () => {
    setShowImageSelector(false);
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  };

  // 이미지 교체
  const handleChangeImage = () => {
    setShowImageSelector(true);
  };
  
  const onFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      let dataUrl = reader.result;
      await fitPageToFrame(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleAddOverlayImage = () => {
    setSelCaptionIdx(null);
    setSelIconIdx(null);
    setSelOverlayIdx(null);
    setShowTextEditor(false);
    setShowIconSearch(false);
    setSelectedType('image')
    overlayFileInputRef.current.click();
  };
  
  const onOverlayFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const raw = reader.result;
      const newOv = defaultNewOverlayImage(raw);
      setPages(ps =>
        ps.map((pg, idx) =>
          idx === selPageIdx
            ? { ...pg, overlays: [...pg.overlays, newOv] }
            : pg
        )
      );
      // 방금 추가된 인덱스 선택
      setSelOverlayIdx((page?.overlays ?? []).length);
      setSelectedType('overlay');
    };
    reader.readAsDataURL(file);
  };

  const handleAddText = () => {
    setPages(ps => {
      const newCaption = defaultNewCaption();
      const newPages = ps.map((pg, i) =>
        i === selPageIdx
          ? { ...pg, captions: [...pg.captions, newCaption] }
          : pg
      );
      const newIdx = page.captions.length;
      setSelCaptionIdx(newIdx);
      setSelIconIdx(null);
      setSelOverlayIdx(null);
      return newPages;
    });
    setSelectedType('text');
  };

  const handleDelText = () => {
    const result = window.confirm('이 캡션을 삭제할까요?');
    if(!result)
      return;

    setPages(p =>
      p.map((pg, i) =>
        i === selPageIdx
          ? {
              ...pg,
              captions: pg.captions.filter((_, j) => j !== selCaptionIdx),
            }
          : pg
      )
    );
    setSelCaptionIdx(null);
    setSelectedType('image');
  };

  const handleDelIcon = () => {
    const result = window.confirm('이 아이콘을 삭제할까요?');
    if (!result) return;
  
    setPages((ps) =>
      ps.map((pg, idx) =>
        idx === selPageIdx
          ? {
              ...pg,
              icons: pg.icons.filter((_, i) => i !== selIconIdx),
            }
          : pg
      )
    );
  
    setSelIconIdx(null);
    setSelectedType('image');
  };

  const handleDelOverlay = () => {
    if (!window.confirm('이 이미지를 삭제할까요?')) return;

    setPages(ps =>
      ps.map((pg, idx) =>
        idx === selPageIdx
          ? { ...pg, overlays: pg.overlays.filter((_, i) => i !== selOverlayIdx) }
          : pg
      )
    );
    setSelOverlayIdx(null);
    setSelectedType('image');
  };

  // --- 터치 제스처 (핀치 줌, 드래그) ---
  const gesture = useRef({ mode: null });

  const onTouchStart = e => {
    e.stopPropagation();
    if (selectedType === 'image') {
      if (e.touches.length === 1) {
        const { clientX: x, clientY: y } = e.touches[0];
        gesture.current = {
          mode: 'drag',
          start: { x, y },
          orig: { x: page.image.x, y: page.image.y },
        };
      } else if (e.touches.length === 2) {
        const [t1, t2] = e.touches;
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        gesture.current = {
          mode: 'pinch',
          start: {
            dist: Math.hypot(dx, dy),
            angle: Math.atan2(dy, dx),
          },
          orig: { scale: page.image.scale, rotate: page.image.rotate },
        };
      }
    } else if (selectedType === 'text' && caption != null) {
      if (e.touches.length === 1) {
        const { clientX: x, clientY: y } = e.touches[0];
        gesture.current = {
          mode: 'drag-text',
          start: { x, y },
          orig: { x: caption.x, y: caption.y },
        };
      } else if (e.touches.length === 2) {
        const [t1, t2] = e.touches;
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        gesture.current = {
          mode: 'pinch-text',
          start: {
            dist: Math.hypot(dx, dy),
            angle: Math.atan2(dy, dx),
          },
          orig: {
            fontSize: caption.fontSize,
            rotate: caption.rotate,
          },
        };
      }
    } else if (selectedType==='icon' && icon!=null) {
      if (e.touches.length===1) {
        const { clientX:x, clientY:y } = e.touches[0];
        gesture.current = {
          mode: 'drag-icon',
          start: { x, y },
          orig:  { x: icon.x, y: icon.y }
        };
      } else if (e.touches.length===2) {
        const [t1,t2] = e.touches;
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        gesture.current = {
          mode: 'pinch-icon',
          start: {
            dist:  Math.hypot(dx,dy),
            angle: Math.atan2(dy,dx)
          },
          orig: {
            scale:  icon.scale,
            rotate: icon.rotate
          }
        };
      }
    } else if (selectedType === 'overlay' && overlay != null) {
      if (e.touches.length === 1) {
        const { clientX: x, clientY: y } = e.touches[0];
        gesture.current = {
          mode: 'drag-overlay',
          start: { x, y },
          orig: {
            x: overlay.x,
            y: overlay.y
          },
        };
      } else if (e.touches.length === 2) {
        const [t1, t2] = e.touches;
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        gesture.current = {
          mode: 'pinch-overlay',
          start: {
            dist: Math.hypot(dx, dy),
            angle: Math.atan2(dy, dx),
          },
          orig: {
            scale: overlay.scale,
            rotate: overlay.rotate,
          },
        };
      }
    }
  };

  const onTouchMove = e => {
    endHoldPreview();
    e.preventDefault();
    e.stopPropagation();
    const g = gesture.current;
    if (!g.mode) return;

    if (g.mode === 'drag' && e.touches.length === 1) {
      const { clientX: x, clientY: y } = e.touches[0];
      const dx = x - g.start.x;
      const dy = y - g.start.y;
      updateImage({
        x: g.orig.x + dx,
        y: g.orig.y + dy,
      })
    }
    if (g.mode === 'pinch' && e.touches.length === 2) {
      const [t1, t2] = e.touches;
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const angle = Math.atan2(dy, dx);
      const scale = (dist / g.start.dist) * g.orig.scale;
      const rotate = g.orig.rotate + ((angle - g.start.angle) * 180) / Math.PI;
      updateImage({scale, rotate})
    }

    if (g.mode === 'drag-text' && e.touches.length === 1) {
      const { clientX: x, clientY: y } = e.touches[0];
      const dx = x - g.start.x;
      const dy = y - g.start.y;
      updateCaption({
        x: g.orig.x + dx,
        y: g.orig.y + dy
      })
    }
    // --- 기존 onTouchMove 내 pinch-text 분기 전체를 아래 코드로 교체 ---
    if (g.mode === 'pinch-text' && e.touches.length === 2) {
      const [t1, t2] = e.touches;
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const angle = Math.atan2(dy, dx);
    
      // 새 크기/회전
      const newFontSize = (dist / g.start.dist) * g.orig.fontSize;
    
      let rotate = g.orig.rotate + ((angle - g.start.angle) * 180) / Math.PI;
      if (Math.abs(rotate) <= 3) rotate = 0;

      // old center
      const { width: w1, height: h1 } = measureCaption(caption);
      const oldCx = caption.x + w1 / 2;
      const oldCy = caption.y + h1 / 2;

      // new box & new center
      const tempCap = { ...caption, fontSize: newFontSize };
      const { width: w2, height: h2 } = measureCaption(tempCap);
      const newCx = caption.x + w2 / 2;
      const newCy = caption.y + h2 / 2;

      // offset to keep it centered
      const offsetX = oldCx - newCx;
      const offsetY = oldCy - newCy;

      updateCaption({
        fontSize: newFontSize,
        rotate,
        x: caption.x + offsetX,
        y: caption.y + offsetY
      });
    }
    if (g.mode==='drag-icon' && e.touches.length===1) {
      const { clientX:x, clientY:y } = e.touches[0];
      const dx = x - g.start.x;
      const dy = y - g.start.y;
      updateIcon({ x: g.orig.x+dx, y: g.orig.y+dy });
    }
    if (g.mode==='pinch-icon' && e.touches.length===2) {
      const [t1,t2] = e.touches;
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist  = Math.hypot(dx,dy);
      const angle = Math.atan2(dy,dx);
      const newScale  = (dist / g.start.dist) * g.orig.scale;
      const newRotate = g.orig.rotate + ((angle - g.start.angle) * 180)/Math.PI;
      updateIcon({ scale: newScale, rotate: newRotate });
    }
    if (g.mode === 'drag-overlay' && e.touches.length === 1) {
      const { clientX: x, clientY: y } = e.touches[0];
      const dx = x - g.start.x;
      const dy = y - g.start.y;
      updateOverlay({
        x: g.orig.x + dx,
        y: g.orig.y + dy,
      });
    }
    if (g.mode === 'pinch-overlay' && e.touches.length === 2) {
      if(overlay.type!='image') return;
      const [t1, t2] = e.touches;
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const angle = Math.atan2(dy, dx);
      const newScale = (dist / g.start.dist) * g.orig.scale;
      const newRotate = g.orig.rotate + ((angle - g.start.angle) * 180) / Math.PI;
      updateOverlay({
        scale: newScale,
        rotate: newRotate,
      });
    }
  };

  const onTouchEnd = () => {
    gesture.current.mode = null;
  };
  
  // PC용 드래그 시작
  const onMouseDown = e => {
    e.stopPropagation();
    e.preventDefault();
    if (e.button !== 0) return; // 왼쪽 버튼만
    
    if (selectedType === 'image') {
      gesture.current = {
        mode: 'mouse-drag',
        start: { x: e.clientX, y: e.clientY },
        orig: { x: page.image.x, y: page.image.y },
      };
    } else if (selectedType === 'text' && caption != null) {
      isTextDraggingRef.current = false;
      gesture.current = {
        mode: 'mouse-drag-text',
        start: { x: e.clientX, y: e.clientY },
        orig: { x: caption.x, y: caption.y },
      };
    } else if (selectedType==='icon' && icon!=null) {
      gesture.current = {
        mode: 'mouse-drag-icon',
        start: { x: e.clientX, y: e.clientY },
        orig:  { x: icon.x, y: icon.y }
      };
    } else if (selectedType === 'overlay' && overlay != null) {
      gesture.current = {
        mode: 'mouse-drag-overlay',
        start: { x: e.clientX, y: e.clientY },
        orig: {
          x: overlay.x,
          y: overlay.y,
        },
      };
    }
  };

  // 드래그 중
  const onMouseMove = e => {
    endHoldPreview();
    const g = gesture.current;
    if (!g.mode) return;
    // 이미지 드래그
    if (g.mode === 'mouse-drag' && (e.buttons & 1)) {
      const dx = e.clientX - g.start.x;
      const dy = e.clientY - g.start.y;
      updateImage({
        x: g.orig.x + dx,
        y: g.orig.y + dy,
      })
    }
    // 텍스트 드래그
    else if (g.mode === 'mouse-drag-text' && (e.buttons & 1)) {
      const dx = e.clientX - g.start.x;
      const dy = e.clientY - g.start.y;
      isTextDraggingRef.current = true;
      updateCaption({
        x: g.orig.x + dx,
        y: g.orig.y + dy
      })
    } else if (g.mode==='mouse-drag-icon' && (e.buttons & 1)) {
      const dx = e.clientX - g.start.x;
      const dy = e.clientY - g.start.y;
      updateIcon({ x: g.orig.x+dx, y: g.orig.y+dy });
    } else if (g.mode === 'mouse-drag-overlay' && (e.buttons & 1)) {
      const dx = e.clientX - g.start.x;
      const dy = e.clientY - g.start.y;
      updateOverlay({
        x: g.orig.x + dx,
        y: g.orig.y + dy,
      });
    }
  };

  // 드래그 종료
  const onMouseUp = () => {
    gesture.current.mode = null;
  };

  // 휠 이벤트로 스케일 / 회전
  const onWheel = e => {
    endHoldPreview();
    e.stopPropagation();
    const delta = -e.deltaY; // 휠 올리면 양수
    const fine = e.altKey ? 0.1 : 1;

    if (selectedType === 'image') {
      if (e.shiftKey) {
        // Shift+휠 → 회전
        updateImage({
          rotate: image.rotate + delta * -0.05 * fine
        })
      } else {
        // 휠 → 스케일
        updateImage({
          scale: Math.max(0.03, image.scale + delta * 0.0005 * fine)
        })
      }
    } else if (selectedType === 'text' && caption != null) {
      if (e.shiftKey) {
        // Shift+휠 → 텍스트 회전
        updateCaption({
          rotate: caption.rotate + delta * -0.05 * fine
        })
      } else {
        // 휠 → 폰트 크기
        const newFontSize = Math.max(1, caption.fontSize + delta * 0.02 * fine);

        const { width: w1, height: h1 } = measureCaption(caption);
        const cx1 = caption.x + w1 / 2;
        const cy1 = caption.y + h1 / 2;

        const tempCap = { ...caption, fontSize: newFontSize };
        const { width: w2, height: h2 } = measureCaption(tempCap);
        const cx2 = caption.x + w2 / 2;
        const cy2 = caption.y + h2 / 2;

        const dx = cx1 - cx2;
        const dy = cy1 - cy2;

        updateCaption({
          fontSize: newFontSize,
          x: caption.x + dx,
          y: caption.y + dy
        });
      }
    } else if (selectedType==='icon' && icon!=null) {
      const delta = -e.deltaY;
      const fine  = e.altKey ? 0.1 : 1;
      if (e.shiftKey) {
        // Shift+휠 → 회전
        const r = icon.rotate + delta * -0.05 * fine;
        updateIcon({ rotate: r });
      } else {
        // 휠 → 스케일
        const s = Math.max(0.03, icon.scale + delta * 0.001 * fine);
        updateIcon({ scale: s });
      }
    } else if (selectedType === 'overlay' && overlay != null) {
      if(overlay.type!='image') return;
      
      const delta = -e.deltaY;
      const fine = e.altKey ? 0.1 : 1;
      if (e.shiftKey) {
        // 회전
        updateOverlay({
          rotate: overlay.rotate + delta * -0.05 * fine,
        });
      } else {
        // 스케일
        updateOverlay({
          scale: Math.max(
            0.03,
            overlay.scale + delta * 0.001 * fine
          ),
        });
      }
    }
  };

  function parseSvgString(source) {
    const parser = new DOMParser();
    const doc    = parser.parseFromString(source, 'image/svg+xml');
    const svgEl  = doc.querySelector('svg');
    if (!svgEl) return null;
  
    // 1) viewBox 같은 주요 속성 복사
    const attrs = {};
    for (let i = 0; i < svgEl.attributes.length; i++) {
      const { name, value } = svgEl.attributes[i];
      attrs[name] = value;
    }
  
    // 2) <svg>...</svg> 사이의 inner HTML
    const inner = svgEl.innerHTML;
    return { attrs, inner };
  }

  function extractSvgColors(svgText) {
    const colors = new Set();
    let match;
  
    // 1) inline 속성: fill="…" 또는 stroke="…"
    const attrRegex = /\b(?:fill|stroke)=["']([^"']+)["']/gi;
    while ((match = attrRegex.exec(svgText))) {
      const c = match[1].trim();
      if (/^(#|rgb)/i.test(c)) colors.add(c);
    }
  
    // 2) style="… fill: #xxxxxx; stroke: #yyyyyy; …"
    const styleAttrRegex = /style=["']([^"']+)["']/gi;
    while ((match = styleAttrRegex.exec(svgText))) {
      for (const decl of match[1].split(';')) {
        const [prop, val] = decl.split(':').map(s => s.trim());
        if ((prop === 'fill' || prop === 'stroke') && /^(#|rgb)/i.test(val)) {
          colors.add(val);
        }
      }
    }
  
    // 3) <style> 블록 안의 CSS: #RRGGBB, #RGB, rgb(...) 전부
    // 헥사코드
    const hexRegex = /#(?:[0-9A-Fa-f]{3}){1,2}\b/g;
    while ((match = hexRegex.exec(svgText))) {
      colors.add(match[0]);
    }
    // rgb(...) 포맷
    const rgbRegex = /rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)/g;
    while ((match = rgbRegex.exec(svgText))) {
      colors.add(match[0]);
    }
  
    return Array.from(colors);
  }

  async function extractDominantColors(
    raw,
    pageIdx,
    {
      k = 6,
      maxIterations = 10,
      resolution = 100,
      minDistance = 50,
      brightnessThreshold = 100
    } = {}
  ) {
    // 1) 이미지 로드 → 캔버스 세팅 → 중앙 정방형 크롭 → resolution×resolution 으로 리사이즈
    const img = new Image();
    img.src = raw;
    await img.decode();
    const canvas = document.createElement('canvas');
    const ctx    = canvas.getContext('2d');
    const { naturalWidth: W, naturalHeight: H } = img;
  
    const size = Math.min(W, H);
    const sx   = Math.floor((W - size) / 2);
    const sy   = Math.floor((H - size) / 2);

    canvas.width  = resolution;
    canvas.height = resolution;
    ctx.drawImage(
      img,
      sx, sy, size, size,         // src x, y, w, h
      0, 0, resolution, resolution // dst x, y, w, h
    );
  
    // 2) 픽셀 전부 수집 → 밝기 threshold 필터링
    const data = ctx.getImageData(0, 0, resolution, resolution).data;
    const pixels = [];
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
      if (a < 128) continue;  // 투명 픽셀 제외
  
      const brightness = (r + g + b) / 3;
      if (brightness < brightnessThreshold || brightness > 255 - brightnessThreshold) {
        // 너무 어둡거나 너무 밝으면 제외
        continue;
      }
  
      pixels.push([r, g, b]);
    }
  
    // 3) 초기 centroid: 앞에서 k개 픽셀로 결정
    let centroids = pixels.slice(0, k).map(p => [p[0], p[1], p[2]]);
    let clusters;
  
    // 4) K-means 반복
    for (let iter = 0; iter < maxIterations; iter++) {
      clusters = Array.from({ length: k }, () => []);
      for (const p of pixels) {
        let best = 0, bestDist = Infinity;
        centroids.forEach((c, idx) => {
          const d = (p[0]-c[0])**2 + (p[1]-c[1])**2 + (p[2]-c[2])**2;
          if (d < bestDist) { bestDist = d; best = idx; }
        });
        clusters[best].push(p);
      }
      let moved = false;
      centroids = centroids.map((c, idx) => {
        const pts = clusters[idx];
        if (!pts.length) return c;
        const sum = pts.reduce((acc, v) => [acc[0]+v[0], acc[1]+v[1], acc[2]+v[2]], [0,0,0]);
        const avg = sum.map(v => Math.round(v / pts.length));
        if (avg[0] !== c[0] || avg[1] !== c[1] || avg[2] !== c[2]) moved = true;
        return avg;
      });
      if (!moved) break;
    }
  
    // 5) HSV 채도 계산 → 내림차순 정렬
    const satList = centroids.map(c => {
      const [r, g, b] = c.map(v => v / 255);
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const s = max === 0 ? 0 : (max - min) / max;
      return { rgb: c, sat: s };
    }).sort((a, b) => b.sat - a.sat);
    const sortedRGB = satList.map(item => item.rgb);
  
    // 6) RGB 거리 필터링
    const distinct = [];
    for (const c of sortedRGB) {
      if (distinct.length >= k) break;
      const ok = distinct.every(dc => {
        const dr = dc[0] - c[0], dg = dc[1] - c[1], db = dc[2] - c[2];
        return Math.sqrt(dr*dr + dg*dg + db*db) >= minDistance;
      });
      if (ok) distinct.push(c);
    }
  
    // 7) HEX 변환 및 state 세팅
    const toHex = v => v.toString(16).padStart(2, '0');
    const hexDominants = distinct.map(c =>
      `#${toHex(c[0])}${toHex(c[1])}${toHex(c[2])}`
    );

    // 1) refill the palette with ONLY the new dominants
    hexDominants.forEach((hex, domIdx) => {
      setColorPalettesByDominant(pageIdx, domIdx, hex);
    });

    // 2) trim off any leftover slots
    setStepProps(prev => {
      const next = [...prev];

      // 1) 기존 pagePalettes 복사 (없으면 빈 배열)
      const pagePalettes = Array.isArray(next[8].pagePalettes)
        ? [...next[8].pagePalettes]
        : [];

      // 2) 해당 인덱스 팔레트 꺼내기 (없으면 기본 구조)
      const pal = pagePalettes[pageIdx]
        ? { ...pagePalettes[pageIdx] }
        : { dominant: [], lighten: [], darken: [] };
        
      // 3) slice 로 길이 맞추기
      const n = hexDominants.length;
      pal.dominant = pal.dominant.slice(0, n);
      pal.lighten  = pal.lighten .slice(0, n);
      pal.darken   = pal.darken  .slice(0, n);

      // 4) 복사해둔 배열에 다시 넣고
      pagePalettes[pageIdx] = pal;

      // 5) stepProps[8] 에 덮어쓰기
      next[8] = {
        ...next[8],
        pagePalettes
      };

      return next;
    });
    
    const prevIdx  = pages[pageIdx]?.selectedDominant ?? 0;
    const newIdx   = 0;
    const oldPal   = stepProps[8].pagePalettes[pageIdx] || { dominant: [], lighten: [], darken: [] };
    const oldDom   = oldPal.dominant[prevIdx];
    const newDom   = hexDominants[newIdx];
    
    const TINT_STEPS = Array.from({ length: 9 }, (_, i) => (i + 1) * 0.1);
    applyChangedDominantColor(pageIdx, oldDom, newDom);
    
    // 5-2) lighten tints 교체
    TINT_STEPS.forEach(v => {
      const oldL = lightenColor(oldDom, v);
      const newL = lightenColor(newDom, v);
      applyChangedDominantColor(pageIdx, oldL, newL);
    });

    // 5-3) darken tints 교체
    TINT_STEPS.forEach(v => {
      const oldD = darkenColor(oldDom, v);
      const newD = darkenColor(newDom, v);
      applyChangedDominantColor(pageIdx, oldD, newD);
    });

    // 6) selectedDominant 인덱스 재설정
    setPages(ps =>
      ps.map((pg, i) =>
        i === pageIdx
          ? { ...pg, selectedDominant: newIdx }
          : pg
      )
    );
  }

  function setColorPalettesByDominant(pageIdx, domIdx, newDom) {
    const TINT_STEPS = Array.from({ length: 9 }, (_, i) => (i + 1) * 0.1);

    setStepProps(prev => {
      const next = [...prev];
    
      // 1) 기존 pagePalettes 복사
      const pagePalettes = Array.isArray(next[8].pagePalettes)
        ? [...next[8].pagePalettes]
        : [];
    
      // 2) 해당 인덱스 팔레트 꺼내오기
      const {
        dominant = [],
        lighten  = [],
        darken   = []
      } = pagePalettes[pageIdx] || {};
    
      // 3) 바꾸기 전/후 dominant
      const oldDom   = dominant[domIdx];
      const newDominant = [...dominant];
      newDominant[domIdx] = newDom;
    
      // 4) 새 dominant 으로 tint 재생성
      const newLighten = newDominant.map(col =>
        TINT_STEPS.map(v => lightenColor(col, v))
      );
      const newDarken = newDominant.map(col =>
        TINT_STEPS.map(v => darkenColor(col, v))
      );
    
      // 5) 실제 페이지 요소 색상 교체
      if (oldDom) {
        applyChangedDominantColor(pageIdx, oldDom, newDom);
        TINT_STEPS.forEach(v => {
          const oldL = lightenColor(oldDom, v);
          const newL = lightenColor(newDom, v);
          applyChangedDominantColor(pageIdx, oldL, newL);
    
          const oldD = darkenColor(oldDom, v);
          const newD = darkenColor(newDom, v);
          applyChangedDominantColor(pageIdx, oldD, newD);
        });
      }
    
      // 6) 복사해둔 배열에 덮어쓰기
      pagePalettes[pageIdx] = {
        dominant: newDominant,
        lighten:  newLighten,
        darken:   newDarken
      };
    
      // 7) stepProps[8].pagePalettes 에 최종 반영
      next[8] = {
        ...next[8],
        pagePalettes
      };
    
      return next;
    });    
  }

  function applyChangedDominantColor(pageIdx, oldColor, newColor) {
    setPages(ps => {
      // ① 전체 pages 복제
      const nextPages = [...ps];
      // ② 해당 페이지만 JSON → string replace → object 변환
      const pg = nextPages[pageIdx];
      const json = JSON.stringify(pg);
      const replaced = json.replaceAll(oldColor, newColor);
      nextPages[pageIdx] = JSON.parse(replaced);
      return nextPages;
    });
  }

  function hexToRgb(hex='#000000') {
    if (typeof hex !== 'string' || !hex.startsWith('#')) {
      console.warn('hexToRgb got invalid input, defaulting to #000000:', hex);
      hex = '#000000';
    }
    const bigint = parseInt(hex.slice(1), 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  }
  
  function rgbToHex(r, g, b) {
    const toHex = (c) => c.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  
  // 1. 밝게 만들기 (value = 1 일 때 흰색)
  function lightenColor(color, value) {
    const { r, g, b } = hexToRgb(color);
    const newR = Math.round(r + (255 - r) * value);
    const newG = Math.round(g + (255 - g) * value);
    const newB = Math.round(b + (255 - b) * value);
    return rgbToHex(newR, newG, newB);
  }
  
  // 2. 어둡게 만들기 (value = 1 일 때 검은색)
  function darkenColor(color, value) {
    const { r, g, b } = hexToRgb(color);
    const newR = Math.round(r * (1 - value));
    const newG = Math.round(g * (1 - value));
    const newB = Math.round(b * (1 - value));
    return rgbToHex(newR, newG, newB);
  }

  async function template1(targetPageIdx = null) {
    if (pages.length === 0) return;
    await document.fonts.ready; // 폰트 로딩 대기

    setIsTemplateLoading(true);

    const pageIndices = 
    targetPageIdx !== null
      ? [ targetPageIdx ]
      : pages.map((_, i) => i);
  
    for (const pi of pageIndices) {
      flushSync(() => setSelPageIdx(pi));
      await waitForRender();

      const page = pages[pi];
      const palettes = stepProps[8].pagePalettes[pi] || { dominant: [], lighten: [], darken: [] };
      const domIdx = page.selectedDominant || 0;

      if (page.isTitle) {
        const tagsIdx = page.captions.findIndex(c => c.type === 'tags');
        const titleIdx = page.captions.findIndex(c=>c.type==='title');
        const descIdx = page.captions.findIndex(c=>c.type==='desc');

        const lines = page.captions[titleIdx].text.split('\n');
        const maxWords = Math.max(
          ...lines.map(line =>
            [...line].reduce((sum, ch) => sum + (ch === ' ' ? 0.3 : 1), 0)
          )
        );
        const descWords = page.captions[descIdx].text.length;
        
        let titleAdjust = 0;
        let descAdjust = 0;
        let titlePadding = 40;
        let descPadding = 40;

        if(maxWords<5)
          titlePadding=40+(5-maxWords)*(20+(5-maxWords)*4)

        if(descWords<15)
          descPadding=40+(15-descWords)*15

        titleAdjust = Math.pow(Math.abs((6-maxWords)),2) * (maxWords>6?2:-1);
        descAdjust = Math.pow(maxWords,2)/5;

        // 1) tags 스타일링
        const tagsY = 37;
        
        if (tagsIdx >= 0) {
          updateCaption(
            {
              x: 35,
              y: tagsY,
              rotate: 0,
              fontSize: 34,
              fontWeight: 600,
              color: "#000000",
              fontFamily: "Hi Melody",
              strokeWidth: 8,
              strokeColor: "#ffffff",
              align: "left",
              useFillGradient: true,
              fillGradient: {
                startColor: palettes.dominant[domIdx],
                endColor: palettes.darken[domIdx]?.[4],
              },
              useStrokeGradient: false,
              strokeGradient: { startColor: palettes.dominant[domIdx] || '#ffffff', endColor: palettes.darken[domIdx]?.[2] || '#000000' },
              shadowEnabled: true,
              shadowColor: palettes.darken[domIdx]?.[1],
              shadowOffsetX: 3,
              shadowOffsetY: 2,
              outerStrokeColor: "#ffffff",
              outerStrokeWidth: 0
            },
            pi,
            tagsIdx
          );
          await waitForRender(); // ← 렌더링 반영 대기
        }
    
        // 2) title 스타일링 & 위치 계산
        const tagsBBox = textRefs.current[tagsIdx].getBBox();
        const tagsH    = tagsBBox.height;
        const titleY = tagsY + tagsH + 20;

        if (titleIdx >= 0) {
          // tags의 bbox를 읽어서 y₀, h₀를 구함
          
          updateCaption(
            {
              y: titleY + titleAdjust,
              rotate: 0,
              fontWeight: "900",
              color: "#ffffff",
              fontFamily: "Gamja Flower",
              strokeWidth: 12,
              strokeColor: palettes.dominant[domIdx],
              align: "center",
              useFillGradient: true,
              fillGradient: {
                startColor: "#ffffff",
                endColor: palettes.lighten[domIdx]?.[4]
              },
              useStrokeGradient: true,
              strokeGradient: {
                startColor: palettes.dominant[domIdx],
                endColor: palettes.darken[domIdx]?.[2]
              },
              shadowEnabled: true,
              shadowColor: palettes.darken[domIdx]?.[4],
              shadowOffsetX: 4,
              shadowOffsetY: 3,
              outerStrokeColor: "#ffffff",
              outerStrokeWidth: 36
            },
            pi,
            titleIdx
          );
          await waitForRender();             // ← 렌더링 반영
          await fillCaptionWidth(pi, titleIdx, titlePadding);
          await waitForRender();             // ← 리사이즈 후 렌더링 반영
        }
    
        // 3) desc 스타일링 & 위치 계산
        const titleBBox = textRefs.current[titleIdx].getBBox();
        const titleH    = titleBBox.height;

        if (descIdx >= 0) {
          const descY = titleY + titleH + 40;
          updateCaption(
            {
              y: descY + descAdjust,
              rotate: 0,
              fontWeight: "500",
              color: "#ffffff",
              fontFamily: "Gamja Flower",
              strokeWidth: 8,
              strokeColor: palettes.dominant[domIdx],
              align: "center",
              useFillGradient: false,
              fillGradient: { startColor: '#ffffff', endColor: '#000000' },
              useStrokeGradient: true,
              strokeGradient: {
                startColor: palettes.dominant[domIdx],
                endColor: palettes.darken[domIdx]?.[2]
              },
              shadowEnabled: true,
              shadowColor: palettes.darken[domIdx]?.[5],
              shadowOffsetX: 2,
              shadowOffsetY: 1,
              outerStrokeColor: "#ffffff",
              outerStrokeWidth: 20
            },
            pi,
            descIdx
          );
          await waitForRender();
          await fillCaptionWidth(pi, descIdx, descPadding);
          await waitForRender();
        }
      }

      const createByIdx = page.captions.findIndex(c => c.type === 'createBy');
      if (createByIdx >= 0) {
        updateCaption(
          {
            x: 27,
            y: 577,
            fontSize: 12,
            fontWeight: 400,
            color: "#404040",
            fontFamily: "Pretendard",
            useStrokeGradient: false,
            strokeWidth: 2.5,
            strokeColor: "#ffffff",
            shadowEnabled: false,
            align: "left",
            outerStrokeWidth: 0
          },
          pi,
          createByIdx
        );
        await waitForRender();
      }
  
      // 5) 그 외 일반 캡션
      for (let ci = 0; ci < page.captions.length; ci++) {
        const cap = page.captions[ci];
        if (["tags", "title", "desc", "createBy"].includes(cap.type)) continue;
        const capWords = cap.text.length;
        let capPadding = 40;
        if(capWords<15)
          capPadding=40+(15-capWords)*15

        updateCaption(
          {
            fontFamily: "Single Day",
            fontWeight: 500,
            useStrokeGradient: true,
            strokeColor: palettes.dominant[domIdx],
            strokeWidth: 8,
            strokeGradient: {
              startColor: palettes.dominant[domIdx],
              endColor: palettes.darken[domIdx]?.[2]
            },
            shadowEnabled: false,
            shadowColor: palettes.darken[domIdx]?.[5],
            shadowOffsetX: 2,
            shadowOffsetY: 1,
            outerStrokeWidth: 15
          },
          pi,
          ci
        );
        await waitForRender();
        await fillCaptionWidth(pi, ci, capPadding);
        await waitForRender();
      }
    }
  
    // 마지막으로 초기 페이지로 돌아가기
    if (targetPageIdx === null) {
      flushSync(() => template1(0));
    }

    setIsTemplateLoading(false);
  }

  async function whenCategoryChanged(category) {
    // 1) 카테고리별 이미지 raw 결정
    let raw;
    switch (category) {
      case "음식점":      raw = "images/category_restaurant.png";      break;
      case "주점":        raw = "images/category_alcohol.png";         break;
      case "카페디저트":  raw = "images/category_cafe.png";            break;
      case "팝업스토어":  raw = "images/category_popup.png";           break;
      case "오락액티비티": raw = "images/category_activity.png";       break;
      case "문화예술":    raw = "images/category_culture.png";         break;
      case "휴식힐링":    raw = "images/category_nature.png";          break;
      case "숙소":        raw = "images/category_accommodation.png";   break;
      case "-":
      default:            raw = "";  // 미지정은 빈값
    }
  
    let newIdx = -1;
    const initY = 447;
    const initScale = 0.21667;
  
    // 2) 동기적으로 state 갱신
    flushSync(() => {
      setPages(ps =>
        ps.map((pg, pi) => {
          if (pi !== selPageIdx) return pg;
  
          // — “미지정”이면 해당 타입 오버레이 전부 삭제
          if (category === "-") {
            return {
              ...pg,
              overlays: pg.overlays.filter(ov => ov.type !== "category"),
              category
            };
          }
  
          // — 기존에 type==='category' 인 게 있나 찾고
          const catIdx = pg.overlays.findIndex(ov => ov.type === "category");
          if (catIdx >= 0) {
            // 업데이트 모드
            newIdx = catIdx;
            const newOverlays = pg.overlays.map((ov, i) =>
              i === catIdx ? { ...ov, raw, y:initY, scale:initScale } : ov
            );
            return {
              ...pg,
              overlays: newOverlays,
              category
            };
          } else {
            // 추가 모드
            const ov = defaultNewOverlayImage(raw, "category");
            ov.y = initY;
            ov.scale = initScale;
            ov.zIndex = nextZ();
  
            newIdx = pg.overlays.length;
            return {
              ...pg,
              overlays: [...pg.overlays, ov],
              category
            };
          }
        })
      );
    });

    await waitForRender();

    if (category !== "-" && newIdx >= 0) {
      centerOverlayX(selPageIdx, newIdx);
    }
  }

  async function whenSourceChanged(source, authorId='출처') {
    // 1) source → raw + 초기 x,y,scale 매핑
    let raw, initX, initY, initScale;

    switch (source) {
      case 'naver':
        raw       = 'images/naver_small.svg';
        initX     =   4;
        initY     = 573;
        initScale =  0.60;
        break;
      case 'blog':
        raw       = 'images/blog.png';
        initX     = -134;
        initY     = 435;
        initScale = 0.045;
        break;
      case 'instagram':
        raw       = 'images/instagram_color.svg';
        initX     =   4;
        initY     = 573;
        initScale =  0.60;
        break;
      default:
        raw = '';
    }
  
    // 2) flushSync 으로 동기 상태 업데이트
    flushSync(() => {
    setPages(ps =>
      ps.map((pg, pi) => {
        if (pi !== selPageIdx) return pg;

        // 1) “-” 선택시, source 오버레이·createBy 캡션 모두 제거
        if (source === '-') {
          return {
            ...pg,
            overlays: pg.overlays.filter(ov => ov.type !== 'source'),
            captions: pg.captions.filter(cap => cap.type !== 'createBy'),
            source
          };
        }

        // 2) source overlay 처리
        const srcIdx = pg.overlays.findIndex(ov => ov.type === 'source');
        let newOverlays;
        if (srcIdx >= 0) {
          // 이미 있으면 raw + 위치/스케일만 교체
          newOverlays = pg.overlays.map((ov, i) =>
            i === srcIdx
              ? {
                  ...ov,
                  raw,
                  x:     initX,
                  y:     initY,
                  scale: initScale
                }
              : ov
          );
        } else {
          // 없으면 새로 추가
          const ov = defaultNewOverlayImage(raw, 'source');
          ov.zIndex = nextZ();
          ov.x      = initX;
          ov.y      = initY;
          ov.scale  = initScale;
          newOverlays = [...pg.overlays, ov];
        }

        // 3) createBy caption 처리
        const capIdx = pg.captions.findIndex(cap => cap.type === 'createBy');
        let newCaptions;
        if (capIdx >= 0) {
          // 이미 있으면 텍스트 + 스타일만 교체
          newCaptions = pg.captions.map((cap, i) =>
            i === capIdx
              ? {
                  ...cap,
                  text:               authorId!='출처'?authorId:cap.text,
                  x:                  27,
                  y:                  577,
                  fontSize:           12,
                  fontWeight:         400,
                  color:              "#404040",
                  fontFamily:         "Pretendard",
                  useStrokeGradient:  false,
                  strokeWidth:        2.5,
                  strokeColor:        "#ffffff",
                  shadowEnabled:      false,
                  align:              "left",
                  outerStrokeWidth:   0
                }
              : cap
          );
        } else {
          // 없으면 새로 추가
          const cap = {
            ...defaultNewCaption(authorId, 'createBy'),
            x:                  27,
            y:                  577,
            fontSize:           12,
            fontWeight:         400,
            color:              "#404040",
            fontFamily:         "Pretendard",
            useStrokeGradient:  false,
            strokeWidth:        2.5,
            strokeColor:        "#ffffff",
            shadowEnabled:      false,
            align:              "left",
            outerStrokeWidth:   0
          };
          newCaptions = [...pg.captions, cap];
        }

        // 4) 최종 리턴
        return {
          ...pg,
          overlays: newOverlays,
          captions: newCaptions,
          source
        };
      })
    );
  });
  }

  const handleGlobalKeyDown = (e) => {
    // Delete 키: 선택된 요소 삭제
    if (e.key === 'Delete') {
      e.preventDefault();
      if (caption)      handleDelText();
      else if (icon)    handleDelIcon();
      else if (overlay) handleDelOverlay();
      return;
    }
  
    // 화살표 키: 좌표 이동
    let dx = 0, dy = 0;
    switch (e.key) {
      case 'ArrowLeft':  dx = -1; break;
      case 'ArrowRight': dx =  1; break;
      case 'ArrowUp':    dy = -1; break;
      case 'ArrowDown':  dy =  1; break;
      default: return;
    }
    e.preventDefault();
    const step = e.shiftKey ? 20 : 1;
    dx *= step;  dy *= step;
  
    if (selectedType === 'image') {
      updateImage({ x: image.x + dx, y: image.y + dy });
    } else if (selectedType === 'text' && caption) {
      updateCaption({ x: caption.x + dx, y: caption.y + dy });
    } else if (selectedType === 'icon' && icon) {
      updateIcon({ x: icon.x + dx, y: icon.y + dy });
    } else if (selectedType === 'overlay' && overlay) {
      updateOverlay({ x: overlay.x + dx, y: overlay.y + dy });
    }
  };
  
  // 2) initialConti → pages 초기화 로직 분리
  async function initializeFromInitialConti() {
    // z-index 카운터
    let zCounter = 1;
    const nextZ = () => zCounter++;
  
    // 1) initialConti → pages 뼈대 생성 (raw, captions, overlays 모두 세팅)
    const mapped = initialConti.map(item => {
      // 1-a) raw 포함
      const raw = normalizeBase64(item.image);
      const page = defaultNewPage(raw);
      page.isTitle  = item.isTitle;
      page.source   = item.source   || '-';
      page.category = item.category || '-';
      page.image_index = item.image_index;
  
      // 1-b) captions 세팅
      let caps = [];
      if (item.isTitle) {
        caps = [
          defaultNewCaption(item.title,    'title'),
          defaultNewCaption(item.tags.join(' '),     'tags'),
          defaultNewCaption(item.desc,     'desc'),
          defaultNewCaption(item.createBy, 'createBy')
        ].map(cap => ({ ...cap, zIndex: nextZ() }));
      } else {
        if(item.caption){
          caps.push({
            ...defaultNewCaption(item.caption, 'caption'),
            zIndex: nextZ()
          });
        }
        caps.push({
          ...defaultNewCaption(item.createBy, 'createBy'),
          zIndex: nextZ()
        });
      }
      page.captions = caps;
  
      // 1-c) overlays 세팅
      const overlays = [];
  
      // — source overlay
      if (item.source === 'naver' || item.source === 'blog' || item.source === 'instagram') {
        let rawSrc, initX, initY, initScale;
        if (item.source === 'naver') {
          rawSrc       = 'images/naver_small.svg';
          initX     =   4;
          initY     = 573;
          initScale =  0.60;
        } else if (item.source === 'blog') {
          rawSrc    = 'images/blog.png';
          initX     = -134;
          initY     =  435;
          initScale =  0.045;
        } else if (item.source === 'instagram') {
          rawSrc    = 'images/instagram_color.svg';
          initX     =    4;
          initY     =  573;
          initScale =  0.60;
        }
        const ov = defaultNewOverlayImage(rawSrc, 'source');
        ov.zIndex = nextZ();
        ov.x      = initX;
        ov.y      = initY;
        ov.scale  = initScale;
        overlays.push(ov);
      }
  
      // — category overlay
      const rawMap = {
        '음식점':      'images/category_restaurant.png',
        '주점':        'images/category_alcohol.png',
        '카페디저트':  'images/category_cafe.png',
        '팝업스토어':  'images/category_popup.png',
        '오락액티비티':'images/category_activity.png',
        '문화예술':    'images/category_culture.png',
        '휴식힐링':    'images/category_nature.png',
        '숙소':        'images/category_accommodation.png',
      };
      if (item.category && item.category !== '-' && rawMap[item.category]) {
        const ov = defaultNewOverlayImage(rawMap[item.category], 'category');
        ov.zIndex = nextZ();
        ov.y      = 447;
        ov.scale  = 0.21667;
        overlays.push(ov);
      }
  
      page.overlays = overlays;
      return page;
    });
  
    // 2) state에 한 번에 넣고
    setPages(mapped);
  
    // 3) 각 페이지에 대해 fit → palette 추출
    for (let i = 0; i < mapped.length; i++) {
      const raw = mapped[i].image.raw;
      if (!raw) continue;
  
      // 3-a) 프레임에 맞춰 위치/스케일 초기화
      await fitPageToFrame(raw, i);
  
      // 3-b) 이미지가 data:image 형태면 dominant 색 뽑기
      if (raw.startsWith('data:image/')) {
        await extractDominantColors(raw, i);
      }
    }
  }

  useEffect(() => {
    // 1) 페이지 수만큼 팔레트가 모두 채워졌는지 확인
    const allReady =
      pages.length > 0 &&
      pages.every((_, idx) => stepProps[8].pagePalettes[idx]?.dominant?.length > 0);

    if (!didInitialPositioning && allReady && !stepProps[8].file && stepProps[8].result.length==0) {
      template1();
      setDidInitialPositioning(true);
    }
  }, [pages.length, stepProps[8].pagePalettes, didInitialPositioning]);

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () =>
      window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [selectedType, caption, icon, overlay, image]);

  useEffect(() => {
    const currentRaws = pages.map(pg => pg.image.raw);
    if(stepProps[8].file && (pages.map(pg => pg.image.raw).join('|') == stepProps[8].file.map(pg => pg.image.raw).join('|')) && !didInitialPositioning){
      prevImageRaws.current = currentRaws;
      setDidInitialPositioning(true);
      return;
    }

    currentRaws.forEach((raw, idx) => {
      if (
        raw?.startsWith('data:image/') &&
        prevImageRaws.current[idx] !== raw
      ) {
        extractDominantColors(raw, idx);
      }
    });

    prevImageRaws.current = currentRaws;
  }, [
    pages.map(pg => pg.image.raw).join('|')
  ]);

  useEffect(() => {
    if (!frameRef.current) return;
    const { height: fh } = frameRef.current.getBoundingClientRect();
    setScaleFactor(fh / (ratio === '4/5' ? 600 : 480));

    /*if(initialConti.length>0 && initialConti[0].icon_keyword){
      setIconQuery(initialConti[0].icon_keyword);
      handleIconSearch(initialConti[0].icon_keyword);
    }*/

    if(stepProps[8].file && stepProps[8].pages.length==0){
      console.log('aaa');
      setPages(stepProps[8].file);
    }
    else if(stepProps[8].pages.length>0){
      console.log('bbb');
      setPages(stepProps[8].pages);
    }
    else if(initialConti.length>0){
      console.log('ccc');
      initializeFromInitialConti();
    }
    else{
      console.log('ddd');
      addPage();
    }

    return () => {
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
    };
  }, []);

  const flattened = [
    ...page.captions.map((c, i) => ({ type: 'caption', data: c, idx: i })),
    ...page.icons   .map((ic,i) => ({ type: 'icon',    data: ic, idx: i })),
    ...page.overlays.map((ov,i) => ({ type: 'overlay', data: ov, idx: i }))
  ];
  // sort by zIndex (you’ll need to have added `zIndex` to every item)
  flattened.sort((a, b) => a.data.zIndex - b.data.zIndex);
  const palette = stepProps[8].pagePalettes[selPageIdx] || { dominant: [], lighten: [], darken: [] };

  return (
    <div className={styles.feedEditor}>
      {/* 상단 툴바 */}
      <div className={styles.toolbarTop}>
        <button onClick={()=>backFunction()}>뒤로</button>
        <div style={{ display: 'flex', gap:'4px'}}>
          {palette.dominant.map((color, idx) => {
            const currentPage = pages[selPageIdx] || {};
            const isSel = currentPage.selectedDominant === idx;
            return (
              <input
                key={idx}
                type="color"
                className={styles.colorSwatch}
                value={color}
                onChange={e =>
                  setColorPalettesByDominant(selPageIdx, idx, e.target.value)
                }
                onClick={e => {
                  if (isSel) return;

                  // 1) 기존/새 dominant 색 뽑아두기
                  const pg        = pages[selPageIdx];
                  const oldIdx    = pg.selectedDominant;
                  const newIdx    = idx;
                  const { dominant, lighten = [], darken = [] } =
                    stepProps[8].pagePalettes[selPageIdx] || {};

                  const oldDom = dominant[oldIdx];
                  const newDom = dominant[newIdx];

                  // 2) dominant 직접 교체
                  applyChangedDominantColor(selPageIdx, oldDom, newDom);

                  // 3) tint(lighten/darken)들도 일괄 교체
                  (lighten[oldIdx] || []).forEach((oldLight, li) => {
                    const newLight = (lighten[newIdx] || [])[li];
                    applyChangedDominantColor(selPageIdx, oldLight, newLight);
                  });
                  (darken[oldIdx] || []).forEach((oldDark, di) => {
                    const newDark = (darken[newIdx] || [])[di];
                    applyChangedDominantColor(selPageIdx, oldDark, newDark);
                  });

                  // 4) 이제 selectedDominant 업데이트
                  setPages(ps =>
                    ps.map((p, i) =>
                      i === selPageIdx
                        ? { ...p, selectedDominant: newIdx }
                        : p
                    )
                  );

                  // 5) picker 열림 방지
                  e.preventDefault();
                }}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  cursor: 'pointer',
                  border: isSel ? '2px dashed #000' : `2px solid ${color}`
                }}
              />
            );
          })}
        </div>
        <select
          className={styles.ratioSelect}
          value={ratio}
          onChange={e => {
            setRatio(e.target.value);
            setStepProps(prev => {
              const next = [...prev];
              next[8].ratio = e.target.value;
              return next;
            });
          }}
        >
          {['1/1', '4/5'].map(ratioValue => (
            <option key={ratioValue} value={ratioValue}>
              {ratioValue.replace('/', ' : ')}
            </option>
          ))}
        </select>
        <label className={styles.safeAreaToggle}>
          <input
            type="checkbox"
            checked={showSafeArea}
            onChange={e => setShowSafeArea(e.target.checked)}
          />
          썸네일
        </label>
        <button onClick={handleSave}>다음</button>
      </div>

      {/* {showIconSearch && (
        <div className={styles.iconSearchPanel}>
          <input
            type="text"
            placeholder="검색어를 입력하세요"
            className={styles.iconSearch}
            value={iconQuery}
            onChange={e => setIconQuery(e.target.value)}
            onKeyDown={e => e.key==='Enter' && handleIconSearch()}
          />
          <button onClick={handleIconSearch}>검색</button>
          <button onClick={()=>setShowIconSearch(false)}>닫기</button>

          <div className={styles.iconResults}>
            {isIconLoading ? 
              <div className={styles.loader}/> 
            :
              iconResults.map(renderIconThumb)
            }
          </div>
        </div>
      )} */}

      {/* 상단 미리보기 */}
      <div
        className={styles.frameViewer}
        ref={frameRef}
        style={{ aspectRatio: ratio, userSelect: 'none' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onWheel={onWheel}
      >
        <div
          style={{
            width: 480,              // 내부 기준 너비 480px
            height: ratio === '4/5' ? 600 : 480,
            transform: `scale(${scaleFactor})`,
            transformOrigin: 'top left',
            position: 'relative',    // 내부 절대위치 요소들이 이 안에서 계산되도록
          }}
        >
          <div className={styles.renderArea}>
            {/* 배경 이미지 */}
            {page.image.raw ? (() => {
              const isVideo = page.image.raw.startsWith('data:video/');
              const commonProps = {
                className: styles.noCallout,
                draggable: false,
                onContextMenu: e => e.preventDefault(),
                src: page.image.raw,
                onClick: () => {
                  setSelectedType('image');
                  setSelCaptionIdx(null);
                  setSelIconIdx(null);
                  setSelOverlayIdx(null);
                  setShowIconSearch(false);
                  setShowTextEditor(false);
                },
                onMouseDown: startHoldPreview,
                onMouseUp: endHoldPreview,
                onMouseLeave: endHoldPreview,
                onTouchStart: startHoldPreview,
                onTouchEnd: endHoldPreview,
                onTouchCancel: () => {
                  if (isHolding) endHoldPreview();
                },
                style: {
                  transform: `translate(${page.image.x}px,${page.image.y}px) scale(${page.image.scale}) rotate(${page.image.rotate}deg)`,
                  filter: isHolding
                    ? 'none'
                    : `brightness(${1 + page.image.brightness}) contrast(${1 + page.image.contrast}) saturate(${1 + page.image.saturation})`,
                },
              };
              return isVideo ? (
                <video
                  {...commonProps}
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              ) : (
                <img
                  {...commonProps}
                  alt=""
                />
              );
            })() : (
              <div className={styles.placeholder}>이미지 또는 동영상을 추가해주세요</div>
            )}
            {!isHolding && page.image.warmth !== 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: page.image.warmth < 0
                    ? `rgba(0, 128, 255, ${-page.image.warmth * 0.5})`
                    : `rgba(255, 128, 0, ${page.image.warmth * 0.5})`,
                  pointerEvents: 'none',
                  mixBlendMode: 'overlay',
                }}
              />
            )}

            {flattened.map(item => {
                switch (item.type) {
                  case 'caption': {
                    const cap     = item.data;
                    const idx     = item.idx;
                    const pageIdx = selPageIdx;
                    const { width, height } = measureCaption(cap);
                  
                    // 텍스트 정렬 기준
                    const anchor = cap.align === 'center'
                      ? 'middle'
                      : cap.align === 'right'
                        ? 'end'
                        : 'start';
                    const xBase = cap.align === 'center'
                      ? width / 2
                      : cap.align === 'right'
                        ? width
                        : 0;
                  
                    // 그림자 오프셋의 절반
                    const shadowX = cap.shadowEnabled ? cap.shadowOffsetX / 2 : 0;
                    const shadowY = cap.shadowEnabled ? cap.shadowOffsetY / 2 : 0;
                  
                    return (
                      <svg
                        key={`cap-${pageIdx}-${idx}`}
                        className={`${styles.captionSvg} ${
                          selectedType === 'text' && selCaptionIdx === idx
                            ? styles.active
                            : ''
                        }`}
                        width={width}
                        height={height}
                        overflow="visible"
                        style={{
                          position: 'absolute',
                          top: cap.y,
                          left: cap.x,
                          overflow: 'visible',
                          transform: `rotate(${cap.rotate}deg)`,
                          cursor: 'move',
                          willChange: 'transform',
                          userSelect: 'none',
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          if (isTextDraggingRef.current) {
                            isTextDraggingRef.current = false;
                            return;
                          }
                          if (selectedType === 'text' && selCaptionIdx === idx) {
                            setShowTextEditor(true);
                          } else {
                            setSelectedType('text');
                            setSelCaptionIdx(idx);
                            setSelIconIdx(null);
                            setSelOverlayIdx(null);
                            setShowTextEditor(false);
                          }
                        }}
                      >
                        <defs>
                          {/* 그림자 필터 */}
                          <filter
                            id={`shadowFilt${pageIdx}_${idx}`}
                            filterUnits="userSpaceOnUse"
                            x="-50%" y="-50%"
                            width="200%" height="200%"
                          >
                            <feDropShadow
                              dx={cap.shadowOffsetX}
                              dy={cap.shadowOffsetY}
                              stdDeviation="0"
                              floodColor={cap.shadowColor}
                            />
                          </filter>
                  
                          {/* 내부 스트로크 그라디언트 */}
                          <linearGradient
                            id={`strokeGrad${pageIdx}_${idx}`}
                            gradientUnits="userSpaceOnUse"
                            x1="0" y1="0"
                            x2="0" y2={height}
                          >
                            <stop offset="0%" stopColor={cap.strokeGradient.startColor} />
                            <stop offset="100%" stopColor={cap.strokeGradient.endColor} />
                          </linearGradient>
                  
                          {/* 마스크: 텍스트 형태 흰색으로 */}
                          <mask
                            id={`strokeMask${pageIdx}_${idx}`}
                            maskUnits="userSpaceOnUse"
                            maskContentUnits="userSpaceOnUse"
                            x={-cap.strokeWidth}
                            y={-cap.strokeWidth}
                            width={width + cap.strokeWidth * 2}
                            height={height + cap.strokeWidth * 2}
                          >
                            <text
                              x={xBase}
                              y={cap.fontSize}
                              textAnchor={anchor}
                              fontSize={cap.fontSize}
                              fontFamily={cap.fontFamily}
                              fontWeight={cap.fontWeight}
                              fill="white"
                              stroke="white"
                              strokeWidth={cap.strokeWidth}
                              vectorEffect="non-scaling-stroke"
                              strokeLinejoin="round"
                              strokeLinecap="round"
                            >
                              {cap.text.split('\n').map((line, i) => (
                                <tspan key={i} x={xBase} dy={i === 0 ? 0 : cap.fontSize * 1.15}>
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          </mask>
                  
                          {/* 채우기용 그라데이션 */}
                          <linearGradient
                            id={`fillGrad${pageIdx}_${idx}`}
                            gradientUnits="userSpaceOnUse"
                            x1="0" y1="0"
                            x2="0" y2={height}
                          >
                            <stop offset="0%" stopColor={cap.fillGradient.startColor} />
                            <stop offset="100%" stopColor={cap.fillGradient.endColor} />
                          </linearGradient>
                        </defs>
                  
                        {/** 1) 외부 테두리: 제일 밑, transform으로 이동 **/}
                        {cap.outerStrokeWidth > 0 && (
                          <text
                            transform={`translate(${shadowX}, ${shadowY})`}
                            x={xBase}
                            y={cap.fontSize}
                            textAnchor={anchor}
                            fontSize={cap.fontSize}
                            fontFamily={cap.fontFamily}
                            fontWeight={cap.fontWeight}
                            fill="none"
                            stroke={cap.outerStrokeColor}
                            strokeWidth={cap.outerStrokeWidth}
                            vectorEffect="non-scaling-stroke"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                          >
                            {cap.text.split('\n').map((line, i) => (
                              <tspan key={i} x={xBase} dy={i === 0 ? 0 : cap.fontSize * 1.15}>
                                {line}
                              </tspan>
                            ))}
                          </text>
                        )}
                  
                        {/** 2) 내부 스트로크 + 채우기 + 그림자 **/}
                        <g filter={cap.shadowEnabled ? `url(#shadowFilt${pageIdx}_${idx})` : undefined}>
                          {cap.useStrokeGradient ? (
                            /* 그라데이션 스트로크: rect + mask */
                            <rect
                              x={-cap.strokeWidth}
                              y={-cap.strokeWidth}
                              width={width + cap.strokeWidth * 2}
                              height={height + cap.strokeWidth * 2}
                              fill={`url(#strokeGrad${pageIdx}_${idx})`}
                              mask={`url(#strokeMask${pageIdx}_${idx})`}
                            />
                          ) : (
                            /* 솔리드 스트로크 폴백 */
                            <text
                              x={xBase}
                              y={cap.fontSize}
                              textAnchor={anchor}
                              fontSize={cap.fontSize}
                              fontFamily={cap.fontFamily}
                              fontWeight={cap.fontWeight}
                              fill="none"
                              stroke={cap.strokeColor}
                              strokeWidth={cap.strokeWidth}
                              vectorEffect="non-scaling-stroke"
                              strokeLinejoin="round"
                              strokeLinecap="round"
                            >
                              {cap.text.split('\n').map((line, i) => (
                                <tspan key={i} x={xBase} dy={i === 0 ? 0 : cap.fontSize * 1.15}>
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          )}
                  
                          {/* 3) 채우기 텍스트 (ref 포함) */}
                          <text
                            ref={el => {
                              if (el) textRefs.current[idx] = el;
                            }}
                            x={xBase}
                            y={cap.fontSize}
                            textAnchor={anchor}
                            fontSize={cap.fontSize}
                            fontFamily={cap.fontFamily}
                            fontWeight={cap.fontWeight}
                            fill={cap.useFillGradient
                              ? `url(#fillGrad${pageIdx}_${idx})`
                              : cap.color
                            }
                          >
                            {cap.text.split('\n').map((line, i) => (
                              <tspan key={i} x={xBase} dy={i === 0 ? 0 : cap.fontSize * 1.15}>
                                {line}
                              </tspan>
                            ))}
                          </text>
                        </g>
                      </svg>
                    );
                  }
                  case 'overlay': {
                    const ov  = item.data;
                    const idx = item.idx;
                    return (
                      <img
                        key={ov.id}
                        data-overlay-idx={ov.id}
                        className={`${styles.noCallout} ${
                          selectedType === 'overlay' && selOverlayIdx === idx
                            ? styles.activeOverlay
                            : ''
                        }`}
                        src={ov.raw}
                        draggable={false}
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedType('overlay');
                          setSelOverlayIdx(idx);
                          setSelCaptionIdx(null);
                          setSelIconIdx(null);
                        }}
                        onLoad={() => {
                          if (ov.type === 'category') {
                            centerOverlayX(selPageIdx, idx);
                          }
                        }}
                        style={{
                          transform: `translate(${ov.x}px, ${ov.y}px) scale(${ov.scale}) rotate(${ov.rotate}deg)`,
                          filter: `brightness(${1 + ov.brightness}) contrast(${1 + ov.contrast}) saturate(${1 + ov.saturation})`,
                          cursor: 'move',
                          userSelect: 'none',
                        }}
                      />
                    );
                  }
                  case 'icon': {
                    const ic  = item.data;
                    const idx = item.idx;
            
                    // parse the raw SVG
                    const parsed = parseSvgString(ic.baseSvgText);
                    if (!parsed) return null;
                    let { attrs, inner } = parsed;
            
                    // ensure unique id
                    delete attrs.id;
                    attrs.id = ic.id;
            
                    // swap out fills/strokes
                    for (let i = 0; i < ic.colors.length; i++) {
                      const esc = ic.colors[i].replace(/[#]/g, '\\$&');
                      inner = inner.replace(
                        new RegExp(esc, 'gi'),
                        ic.customColors[i]
                      );
                    }
            
                    const style = {
                      transform: `translate(${ic.x}px, ${ic.y}px) scale(${ic.scale}) rotate(${ic.rotate}deg)`,
                      overflow: "visible",
                      cursor: 'move'
                    };
                    const outlineFilterId = `iconOutline_${ic.id}`;
            
                    return (
                      <svg
                        key={ic.id}
                        data-icon-idx={ic.id}
                        {...attrs}
                        style={style}
                        className={`${styles.iconSvg} ${selIconIdx === idx ? styles.activeIcon : ''}`}
                        overflow="visible"
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedType('icon');
                          setSelIconIdx(idx);
                          setSelCaptionIdx(null);
                          setSelOverlayIdx(null);
                        }}
                      >
                        <defs>
                          <filter
                            id={outlineFilterId}
                            x="-50%" y="-50%"
                            width="200%" height="200%"
                            filterUnits="userSpaceOnUse"
                            primitiveUnits="userSpaceOnUse"
                          >
                            <feMorphology
                              in="SourceAlpha"
                              operator="dilate"
                              radius={ic.strokeWidth}
                              result="MORPH"
                            />
                            <feFlood
                              floodColor={ic.strokeColor}
                              floodOpacity="1"
                              result="FLOOD"
                            />
                            <feComposite
                              in="FLOOD"
                              in2="MORPH"
                              operator="in"
                              result="OUTLINE"
                            />
                            <feMerge>
                              <feMergeNode in="OUTLINE" />
                              <feMergeNode in="SourceGraphic" />
                            </feMerge>
                          </filter>
                        </defs>
            
                        <g filter={`url(#${outlineFilterId})`} className={styles.iconbbox}>
                          <g dangerouslySetInnerHTML={{ __html: inner }} />
                        </g>
            
                        <g style={{
                          filter: `
                            brightness(${1 + ic.brightness})
                            contrast(${1 + ic.contrast})
                            saturate(${1 + ic.saturation})
                          `
                        }}>
                          <g dangerouslySetInnerHTML={{ __html: inner }} />
                        </g>
                      </svg>
                    );
                  }
                  default:
                    return null;
                }
              })}

            {showSafeArea && ratio === '4/5' && (
              <>
                <div
                  className={styles.maskPanel}
                  style={{ left: 0, top: 0, bottom: 0, width: '3.125%' }}
                />
                <div
                  className={styles.maskPanel}
                  style={{ right: 0, top: 0, bottom: 0, width: '3.125%' }}
                />
              </>
            )}
            {showSafeArea && ratio === '1/1' && (
              <>
                <div
                  className={styles.maskPanel}
                  style={{ left: 0, top: 0, bottom: 0, width: '12.5%' }}
                />
                <div
                  className={styles.maskPanel}
                  style={{ right: 0, top: 0, bottom: 0, width: '12.5%' }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* 툴바 */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <button onClick={handleChangeImage}>배경 교체</button>
          <button onClick={handleAddText}>텍스트 추가</button>
          {/* <button onClick={whenIconSearchButtonClicked}>아이콘 추가</button> */}
          <button onClick={handleAddOverlayImage}>이미지 추가</button>
        </div>
        {selectedType === 'image' && 
          <div className={styles.subToolbar}>
            {[
              { key: 'brightness', label: '밝기', min: -0.5, max: 0.5, step: 0.01 },
              { key: 'contrast', label: '대비', min: -0.5, max: 0.5, step: 0.01 },
              { key: 'saturation', label: '채도', min: -1, max: 1, step: 0.01 },
              { key: 'warmth',     label: '온도', min: -0.5, max: 0.5, step: 0.01 }
            ].map(cfg => (
              <div key={cfg.key} className={styles.control}>
                <label>{cfg.label}</label>
                <input
                  type="range"
                  min={cfg.min}
                  max={cfg.max}
                  step={cfg.step}
                  value={page.image[cfg.key]}
                  onChange={e =>
                    updateImage({
                      [cfg.key]: parseFloat(e.target.value)
                    })
                  }
                />
                <label>{page.image[cfg.key]}</label>
                <div className={styles.reset} onClick={()=>{
                  updateImage({
                    [cfg.key]: 0
                  })
                }}>🔄</div>
              </div>
            ))}
            <div className={styles.control}>
              <label>출처</label>
              <select
                className={styles.ratioSelect}
                value={page.source}
                onChange={e =>whenSourceChanged(e.target.value)}
              >
                <option value="-">-</option>
                <option value="naver">네이버</option>
                <option value="blog">블로그</option>
                <option value="instagram">인스타그램</option>
              </select>
              <label>분류</label>
              <select
                className={styles.ratioSelect}
                value={page.category}
                onChange={e =>whenCategoryChanged(e.target.value)}
              >
                <option value="-">-</option>
                <option value="음식점">음식점</option>
                <option value="주점">주점</option>
                <option value="카페디저트">카페디저트</option>
                <option value="팝업스토어">팝업스토어</option>
                <option value="오락액티비티">오락액티비티</option>
                <option value="문화예술">문화예술</option>
                <option value="휴식힐링">휴식힐링</option>
                <option value="숙소">숙소</option>
              </select>
            </div>
            <div className={styles.control}>
              <label>초기화</label>
              <button onClick={()=>whenResetImageButtonClicked()}>배경 초기화</button>
              <button onClick={()=>whenResetCaptionButtonClicked()}>텍스트 초기화</button>
            </div>
          </div>    
        }
        {selectedType === 'icon' && 
          <div className={styles.subToolbar}>
            {icon.colors && icon.colors.length > 0 && (
              <div className={styles.control}>
              <label>색상</label>
              <div className={styles.iconColorFrame}>
                {icon.colors.map((origColor, ci) => {
                  return (
                    <div key={ci} style={{ display: 'flex', flexDirection:'column', gap: '2px' }}>
                      {/* 1) 컬러 피커 */}
                      <input
                        type="color"
                        className={styles.color}
                        value={icon.customColors[ci]}
                        onChange={e => {
                          const newCustom = [...icon.customColors];
                          newCustom[ci] = e.target.value;
                          updateIcon({
                            customColors: newCustom
                          });
                        }}
                      />
                      <div
                        className={styles.reset}
                        onClick={() => {
                          // customColors[ci] 를 원본으로 복원
                          const newCustom = [...icon.customColors];
                          newCustom[ci] = icon.colors[ci];
                          updateIcon({
                            customColors: newCustom
                          });
                        }}
                      >
                        🔄
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
            <div className={styles.control}>
              <label>테두리</label>
              <input
                type="color"
                value={icon.strokeColor}
                className={styles.color}
                onChange={e =>
                  updateIcon({ strokeColor: e.target.value })
                }
              />
              <label>두께</label>
              <input
                type="range"
                min={0} max={30} step={1}
                value={icon.strokeWidth}
                className={styles.range}
                onChange={e =>
                  updateIcon({ strokeWidth: +e.target.value })
                }
              />
              <label>{icon.strokeWidth}</label>
            </div>
            {[
              { key: 'brightness', label: '밝기', min: -0.5, max: 0.5, step: 0.01 },
              { key: 'contrast', label: '대비', min: -0.5, max: 0.5, step: 0.01 },
              { key: 'saturation', label: '채도', min: -1, max: 1, step: 0.01 }
            ].map(cfg => (
              <div className={styles.control} key={cfg.key}>
                <label>{cfg.label}</label>
                <input
                  type="range"
                  min={cfg.min}
                  max={cfg.max}
                  step={cfg.step}
                  value={icon[cfg.key]}
                  onChange={e =>
                    updateIcon({
                      [cfg.key]: parseFloat(e.target.value)
                    })
                  }
                />
                <label>{icon[cfg.key]}</label>
                <div className={styles.reset} onClick={() => updateIcon({[cfg.key]: 0})}>🔄</div>
              </div>
            ))}
            <div className={styles.control}>
              <label>순서</label>
              <button onClick={() => adjustZ(+1)}>↑</button>
              <button onClick={() => adjustZ(-1)}>↓</button>
            </div>
            <div className={styles.control}>
              <label>조작</label>
              <button onClick={handleDuplicate}>복제</button>
              <button onClick={handleDelIcon}>삭제</button>
            </div>
          </div>
        }
        {selectedType === 'text' && 
          <div className={styles.subToolbar}>
            {/* 텍스트 속성 */}
            {caption != null && (
              <>
                <div className={styles.control}>
                  <label>글꼴</label>
                  {(() => {
                    const fontOptions = [
                      { value: 'Pretendard',         label: '프리텐다드' },
                      { value: 'Nanum Gothic',       label: '나눔 고딕' },
                      { value: 'Nanum Myeongjo',     label: '나눔 명조' },
                      { value: 'Nanum Pen Script',   label: '나눔 손글씨' },
                      { value: 'Gowun Dodum',        label: '고운돋움' },
                      { value: 'Gowun Batang',       label: '고운바탕' },
                      { value: 'Do Hyeon',           label: '도현체' },
                      { value: 'Song Myung',         label: '송명체' },
                      { value: 'Black Han Sans',     label: '블랙 한 산스' },
                      { value: 'Stylish',            label: '스타일리시' },
                      { value: 'Hi Melody',          label: '하이 멜로디' },
                      { value: 'Gamja Flower',       label: '감자 꽃' },
                      { value: 'Poor Story',         label: '푸어 스토리' },
                      { value: 'Dongle',             label: '동글체' },
                      { value: 'Jua',                label: '주아체' },
                      { value: 'East Sea Dokdo',     label: '독도체' },
                      { value: 'Single Day',         label: '싱글데이' },
                      { value: 'Gaegu',              label: '개구체' },
                      { value: 'Nanum Brush Script', label: '나눔 붓글씨' },
                      { value: 'Gugi',               label: '구기' },
                      { value: 'Kirang Haerang',     label: '기랑 해랑' },
                      { value: 'Sunflower',          label: '선플라워' },
                      { value: 'Hahmlet',            label: '함렛' },
                    ];

                    return (
                      <select
                        className={styles.ratioSelect}
                        value={caption.fontFamily}
                        onChange={e =>
                          updateCaption({ fontFamily: e.target.value })
                        }
                      >
                        {fontOptions.map(({ value, label }) => (
                          <option key={value} value={value} style={{ fontFamily: value }}>
                            {label}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                  <label>정렬</label>
                  <select
                    className={styles.ratioSelect}
                    value={caption.align}
                    onChange={e =>
                      updateCaption({ align: e.target.value })
                    }
                  >
                    <option value="left">왼쪽</option>
                    <option value="center">가운데</option>
                    <option value="right">오른쪽</option>
                  </select>

                  <div
                    className={styles.ratioSelect}
                    value={caption.fontSize}
                    style={{textAlign:'center', lineHeight:'28px', fontSize:'15px'}}
                  >{caption.fontSize.toFixed(1)}</div>
                </div>

                <div className={styles.control}>
                  <label>텍스트</label>
                  <input
                    type="color"
                    value={caption.color}
                    className={styles.color}
                    onChange={e =>
                      updateCaption({ color: e.target.value })
                    }
                  />
                  <input
                    type="range"
                    min={100}
                    max={900}
                    step={100}
                    value={caption.fontWeight || 400}
                    className={styles.range}
                    onChange={e =>
                      updateCaption({ fontWeight: e.target.value })
                    }
                  />
                  <label>{caption.fontWeight}</label>
                </div>

                <div className={styles.control}>
                  <label style={{textAlign:"right"}}>↳</label>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={caption.useFillGradient}
                    onChange={e => updateCaption({ useFillGradient: e.target.checked })}
                  />
                  <input
                    type="color"
                    className={styles.color}
                    value={caption.fillGradient.startColor}
                    onChange={e => updateCaption({
                      fillGradient: { ...caption.fillGradient, startColor: e.target.value }
                    })}
                  />
                  <input
                    type="color"
                    className={styles.color}
                    value={caption.fillGradient.endColor}
                    onChange={e => updateCaption({
                      fillGradient: { ...caption.fillGradient, endColor: e.target.value }
                    })}
                  />
                </div>
                
                <div className={styles.control}>
                  <label>테두리</label>
                  <input
                    type="color"
                    value={caption.strokeColor}
                    className={styles.color}
                    onChange={e =>
                      updateCaption({ strokeColor: e.target.value })
                    }
                  />
                  <input
                    type="range"
                    min={0}
                    max={20}
                    step={0.5}
                    value={caption.strokeWidth}
                    className={styles.range}
                    onChange={e =>
                      updateCaption({ strokeWidth: e.target.value })
                    }
                  />
                  <label>{caption.strokeWidth}</label>
                </div>

                <div className={styles.control}>
                  <label style={{textAlign:"right"}}>↳</label>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={caption.useStrokeGradient}
                    onChange={e => updateCaption({ useStrokeGradient: e.target.checked })}
                  />
                  <input
                    type="color"
                    className={styles.color}
                    value={caption.strokeGradient.startColor}
                    onChange={e => updateCaption({
                      strokeGradient: { ...caption.strokeGradient, startColor: e.target.value }
                    })}
                  />
                  <input
                    type="color"
                    className={styles.color}
                    value={caption.strokeGradient.endColor}
                    onChange={e => updateCaption({
                      strokeGradient: { ...caption.strokeGradient, endColor: e.target.value }
                    })}
                  />
                </div>

                {/* ───────── 그림자(입체) 효과 ───────── */}
                <div className={styles.control}>
                  <label>그림자</label>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={caption.shadowEnabled}
                    onChange={e => updateCaption({shadowEnabled: e.target.checked })}
                  />
                  <input
                    type="color"
                    className={styles.color}
                    value={caption.shadowColor}
                    onChange={e => updateCaption({shadowColor: e.target.value })}
                  />
                  <input
                    type="number"
                    value={caption.shadowOffsetX}
                    onChange={e => updateCaption({shadowOffsetX: +e.target.value })}
                    style={{ width: 30 }}
                  />
                  <input
                    type="number"
                    value={caption.shadowOffsetY}
                    onChange={e => updateCaption({ shadowOffsetY: +e.target.value })}
                    style={{ width: 30 }}
                  />
                </div>

                <div className={styles.control}>
                  <label>외부</label>
                  <input
                    type="color"
                    className={styles.color}
                    value={caption.outerStrokeColor}
                    onChange={e => updateCaption({ outerStrokeColor: e.target.value })}
                  />
                  <input
                    type="range"
                    className={styles.range}
                    min={0} max={40} step={1}
                    value={caption.outerStrokeWidth}
                    onChange={e => updateCaption({ outerStrokeWidth: +e.target.value })}
                  />
                  <label>{caption.outerStrokeWidth}</label>
                </div>

                <div className={styles.control}>
                  <label>순서</label>
                  <div className={styles.controlButtonFrame}>
                    <button onClick={() => adjustZ(+1)}>↑</button>
                    <button onClick={() => adjustZ(-1)}>↓</button>
                  </div>
                </div>
                <div className={styles.control}>
                  <label>정렬</label>
                  <div className={styles.controlButtonFrame}>
                    <button
                      onClick={() => {
                        centerCaptionX();
                      }}
                    >
                      가로
                    </button>
                    <button
                      onClick={() => {
                        centerCaptionY();
                      }}
                    >
                      세로
                    </button>
                    <button
                      onClick={() => {
                        fillCaptionWidth();
                      }}
                    >
                      채우기
                    </button>
                  </div>
                </div>
                <div className={styles.control}>
                  <label>조작</label>
                  <div className={styles.controlButtonFrame}>
                    <button onClick={handleDuplicate}>복제</button>
                    <button onClick={handleDelText}>삭제</button>
                  </div>
                </div>
              </>
            )}
          </div>
        }
        {selectedType === 'overlay' && overlay != null && (
          <div className={styles.subToolbar}>
            {[
              { key: 'brightness', label: '밝기', min: -0.5, max: 0.5, step: 0.01 },
              { key: 'contrast',   label: '대비', min: -0.5, max: 0.5, step: 0.01 },
              { key: 'saturation', label: '채도', min: -1,   max: 1,   step: 0.01 }
            ].map(cfg => (
              <div className={styles.control} key={cfg.key}>
                <label>{cfg.label}</label>
                <input
                  type="range"
                  min={cfg.min}
                  max={cfg.max}
                  step={cfg.step}
                  value={overlay[cfg.key]}
                  onChange={e =>
                    updateOverlay({
                      [cfg.key]: parseFloat(e.target.value)
                    })
                  }
                />
                <label>{overlay[cfg.key]}</label>
                <div
                  className={styles.reset}
                  onClick={() =>
                    updateOverlay({ [cfg.key]: 0 })
                  }
                >
                  🔄
                </div>
              </div>
            ))}
            <div className={styles.control}>
              <label>순서</label>
              <div className={styles.controlButtonFrame}>
                <button onClick={() => adjustZ(+1)}>↑</button>
                <button onClick={() => adjustZ(-1)}>↓</button>
              </div>
              <label>정렬</label>
              <div className={styles.controlButtonFrame}>
                <button
                  onClick={() => {
                    centerOverlayX();
                  }}
                >
                  가로
                </button>
                <button
                  onClick={() => {
                    centerOverlayY();
                  }}
                >
                  세로
                </button>
              </div>
            </div>
            <div className={styles.control}>
              <label>조작</label>
              <div className={styles.controlButtonFrame}>
                <button onClick={handleDuplicate}>복제</button>
                <button onClick={handleDelOverlay}>삭제</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 썸네일 리스트 */}
      <div className={styles.thumbListFrame}>
        <div className={styles.thumbList}>
          {pages.map((pg, idx) => {
            const usageCount = pages.filter(p => p.image_index === pg.image_index).length;
            return (
              <div
                key={idx}
                className={`${styles.thumbItem} ${
                  idx === selPageIdx ? styles.thumbItemActive : ''
                }`}
              >
                <div
                  className={styles.thumbFrame}
                  onClick={() => selectPage(idx)}
                >
                  {pg.image.raw ? (() => {
                    const isVideo = pg.image.raw.startsWith('data:video/');
                    return isVideo ? (
                      <video
                        src={pg.image.raw}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className={styles.thumbVideo}
                      />
                    ) : (
                      <img src={pg.image.raw} alt="" />
                    );
                  })() : (
                    <div className={styles.thumbPlaceholder} />
                  )}
                  {usageCount > 1 && pg.image_index!=null ? (
                    <div className={styles.screen3} />
                  ) : null}
                </div>
              </div>
            )
          })}
          <button className={styles.thumbAdd} onClick={addPage}>
            ＋
          </button>
        </div>
        <div className={styles.thumbRightFrame}>
          <div className={styles.thumbRightTopText} onClick={() => delPage(selPageIdx)}>삭제</div>
          <div className={styles.thumbRightBottomFrame}>
            <div className={styles.thumbRightBottomText} onClick={() => movePage(selPageIdx, selPageIdx - 1)}>←</div>
            <div className={styles.thumbRightBottomText} onClick={() => movePage(selPageIdx, selPageIdx + 1)}>→</div>
          </div>
        </div>
      </div>

      {showTextEditor && caption != null && (
        <div className={styles.textEditorModal}>
          <textarea
            className={styles.textEditorInput}
            value={caption.text}
            onChange={e =>
              updateCaption({ text: e.target.value })
            }
            onKeyDown={e => e.stopPropagation()}
            rows={5}
          />
          <button onClick={() => setShowTextEditor(false)}>완료</button>
        </div>
      )}

      {showImageSelector && (
        <div
          className={styles.selectedOverlay}
          onClick={() => setShowImageSelector(false)}
        >
          <div className={styles.modalFrame} onClick={e => e.stopPropagation()}>
            <div
              className={styles.selectedImagesContainer}
            >
              {getSelectedImages()
                // 1) 먼저 [raw, originalIdx] 쌍으로 바꿔 줍니다.
                .map((raw, originalIdx) => {
                  const pagesUsing = pages.filter(p => p.image_index === originalIdx);
                  const usageCount = pagesUsing.length;
                  const authorId = stepProps[6].authorIds[originalIdx];

                  return (
                    <div key={originalIdx} className={styles.selectedImageFrame}>
                      <img
                        className={styles.selectedImage}
                        src={raw}
                        alt=""
                        onClick={() => {
                          fitPageToFrame(raw);
                          whenSourceChanged(
                            authorId=='업체등록사진'?'naver':'blog',
                            authorId
                          );
                          setShowImageSelector(false);
                          setPages(ps =>
                            ps.map((p, i) =>
                              i === selPageIdx
                                ? { ...p, image_index: originalIdx }
                                : p
                            )
                          );
                          setSelectedType('image');
                        }}
                      />

                      {usageCount > 1 ? (
                        <>
                          <div className={styles.screen3} style={{ pointerEvents: 'none' }}/>
                          <img className={styles.iconcheckedFilled} alt="checked" src="images/checked_filled.svg" style={{ pointerEvents: 'none' }} />
                        </>
                      ) : usageCount === 1 ? (
                        <>
                          <div className={styles.screen} style={{ pointerEvents: 'none' }}/>
                          <img className={styles.iconcheckedFilled} alt="checked" src="images/checked_filled.svg" style={{ pointerEvents: 'none' }} />
                        </>
                      ) : null}
                    </div>
                  );
                })}

              <div className={styles.modalButtons}>
                <button onClick={handleLoadFromOS}>
                  이미지/영상 불러오기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isRendering &&
        <div className={styles.loadingFrame}>
          <div className={styles.loader}/>
          <div className={styles.loadingText}>
            콘텐츠 렌더링 중...
          </div>
          <div className={styles.loadingText}>
            ({selPageIdx+1}/{pages.length})
          </div>
        </div>
      }

      {isIconDownloading &&
        <div className={styles.loadingFrame}>
          <div className={styles.loader}/>
          <div className={styles.loadingText}>
            아이콘 다운로드 중...
          </div>
        </div>
      }

      {isTemplateLoading &&
        <div className={styles.loadingFrame}>
          <div className={styles.loader}/>
          <div className={styles.loadingText}>
            콘텐츠 자동 생성 중...
          </div>
        </div>
      }

      {/* 히든 파일 입력 */}
      <input
        type="file"
        accept="image/*,video/mp4"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={onFileChange}
      />
      <input
        type="file"
        accept="image/*"
        ref={overlayFileInputRef}
        style={{ display: 'none' }}
        onChange={onOverlayFileChange}
      />
    </div>
  );
}